// Comparison Studio: a full-screen light table for before/after satellite
// imagery. Owns its own MapLibre map (Esri proxy tiles for context, scene
// canvases as georeferenced sources), a filmstrip date axis with A/B markers,
// A | B | Wipe | Blink view modes, an independent change-evidence overlay,
// geo-pinned markup, and PNG export.
//
// All pixel math lives in raster.ts (the old app's engine, ported): per-scene
// percentile stretch, radiometric scene matching, sub-pixel co-registration,
// freeze-unchanged, and the seven change layers.

import type { Polygon } from 'geojson';
import * as maplibregl from 'maplibre-gl';
import type { EntityDto } from './api';
import { post, put } from './api';
import { BASEMAP_LABELS, installBasemaps, setBasemap } from './basemaps';
import { showDrawBar } from './drawbar';
import { isMapEditing } from './editlock';
import { installFirmsImages } from './firms';
import { mapSettled } from './exporter';
import {
  closeMarkup, installMarkupLayers, isMarkupActive, markupEscape, openMarkup, raiseMarkupLayers,
} from './markup';
import {
  activeSheetTitle, exportActiveSheet, isSheetPodOpen, openSheetPod, refreshSheetFrames,
  registerSheetHost, sheetEscape, startDraft, toggleSheetPod, useSheet, type SheetDto,
  type SheetHost,
} from './sheets';
import { isCompactViewport, toast } from './hud';
import { cancelScanDraw, decodeAllBands, decodeImageBands, startScanDraw } from './scan';
import { dayMarks, fmtDayRun, fmtMonthDay, laneKeys, placeLabels } from './strip';
import {
  RAW_BANDS, applySubpixelShift, baselineBandStat, baselineCounts, baselineScene,
  compositeBAIS2, compositeDNBR, compositeDNBR2, compositeHotspot,
  compositeRetardant, compositeScenePair, compositeWaterChange,
  computeBaselineAnomaly, computeChange, computeSarChange,
  computeSpectralAngleChange, estimateSubpixelShift, freezeKeepMask,
  compositeRgb, imageDataToCanvas, radiometricFit, sharpenImage, validShare,
  type BandFits, type BaselineStats, type ChangeResult, type RawScene,
  type SubpixelShift,
} from './raster';

// Freeze-unchanged merge: the BEFORE scene everywhere it has data, replaced by
// the real AFTER pixels where the keep mask says so (freezeKeepMask — the
// change score above a cutoff BELOW the visible threshold, dilated a pixel, so
// faint real change and patch rims survive the freeze) — and ALSO wherever the
// before scene has no data (swath edge), so a partial A never punches holes
// through the B display.
function mergeFrozen(before: ImageData, after: ImageData, changeMask: Uint8Array): ImageData {
  const out = new Uint8ClampedArray(before.data);
  const od = after.data;
  for (let i = 0; i < changeMask.length; i++) {
    const o = i * 4;
    if (changeMask[i] || before.data[o + 3] === 0) {
      out[o] = od[o]!; out[o + 1] = od[o + 1]!;
      out[o + 2] = od[o + 2]!; out[o + 3] = od[o + 3]!;
    }
  }
  return new ImageData(out, before.width, before.height);
}

interface ScanResponse {
  scanId: string;
  bbox: [number, number, number, number];
  bandOrder: string[];
  format?: 'png';          // VIIRS: a rendered image, not a GeoTIFF of bands
  noDataBlack?: boolean[]; // per scene: is black no-data (not in the Day/Night Band)
}

/** One listed scene. SAR entries are PASSES of the chosen orbit and carry the
 *  pass's UTC time and how much of the area it saw. */
interface SceneDate {
  date: string;
  cloud: number | null;
  time?: string;
  coverage?: number;
  // VIIRS entries are PASSES: `date` is the overpass instant (the strip's key),
  // `day` the GIBS image it comes from.
  sat?: string;
  satName?: string;
  satShort?: string;
  part?: 'day' | 'night';
  day?: string;
  approx?: boolean;
}

/** A Sentinel-1 relative orbit over the area — the unit a SAR pair must share. */
interface SarTrack {
  id: string;              // 'D137' = descending relative orbit 137
  direction: 'ascending' | 'descending';
  orbit: number;
  coverage: number;
  passes: number;
  days?: string[];         // UTC days it flew over lately, newest first
  time?: string;           // 'HH:MM' UTC — a track crosses at one time of day
}

interface DatesResponse {
  dates: SceneDate[];
  track?: string | null;
  tracks?: SarTrack[];
}

/** 'x' shows one ADDED scene of the series on its own (the pane in `viewX`). */
type Mode = 'a' | 'b' | 'x' | 'wipe' | 'blink';
/** What a wipe side, a single view or an export frame shows: A and B by ROLE
 *  (they follow a swap), an added scene by its date, or the basemap. */
type Pane = 'a' | 'b' | 'map' | `x:${string}`;
/** The series: the A/B pair plus up to four more scenes. Every one is a
 *  Process API render and ~50 MB of decoded bands, so it stops somewhere. */
const SERIES_MAX = 6;
type Source = 'optical' | 'sar' | 'viirs';
/** Every VIIRS pass (NOAA-20, NOAA-21, Suomi NPP): shown as delivered. */
const isViirs = (s: Source): boolean => s === 'viirs';
type ChangeLayer = 'cva' | 'sam' | 'dwater' | 'dnbr' | 'dnbr2' | 'bais2' | 'hotspot'
                 | 'retardant' | 'sarchange' | 'anomaly';
const RED = '#d93025';

const STYLE_LABELS: Record<string, string> = {
  truecolor: 'True color · natural',
  hdr: 'True color HDR · dynamic range',
  colorir: 'False color IR · vegetation',
  swir: 'SWIR · fire & burn scars',
  burnscar: 'Burn scar composite · SWIR',
  smokefire: 'Smoke & active fire',
  ndvi: 'NDVI · vegetation health',
  nbr: 'NBR · burn condition',
  ndmi: 'NDMI · vegetation moisture',
  ndwi: 'NDWI · open water',
  mndwi: 'MNDWI · water & wet ground',
};

const LAYER_LABELS: Record<ChangeLayer, string> = {
  cva: 'Multispectral difference · any change',
  sam: 'Spectral angle · material change',
  dwater: 'Water & wetness · dMNDWI',
  dnbr: 'Burn severity · dNBR',
  dnbr2: 'Ash & char · dNBR2',
  bais2: 'Burned area · BAIS2',
  hotspot: 'SWIR hotspots · latest scene',
  retardant: 'Retardant & foam · experimental',
  sarchange: 'SAR amplitude change · log-ratio',
  anomaly: 'Baseline anomaly · outside normal range',
};

// Sentinel-1 SAR: its own view styles and its single change layer. Kept apart
// from the optical maps so the Studio can swap the whole option set with source.
const SAR_STYLE_LABELS: Record<string, string> = {
  vvvh: 'VV/VH false color · structure',
  vv: 'VV · co-pol backscatter',
  vh: 'VH · cross-pol backscatter',
};
const OPTICAL_STYLE_LABELS = STYLE_LABELS;
// VIIRS: each "style" is a VIEW — which GIBS product a day pass and a night
// pass show, and so which passes are listed at all. Changing it re-lists.
const VIIRS_STYLE_LABELS: Record<string, string> = {
  daynight: 'Day true color + night lights',
  night: 'Night lights · Day/Night Band',
  thermal: 'Thermal I5 · day + night heat',
  truecolor: 'Day true color',
  firebands: 'Day M11-I2-I1 · fire & burn scars',
  snowwater: 'Day M3-I3-M11 · snow, ice & water',
};
const defaultStyle = (s: Source): string =>
  s === 'sar' ? 'vvvh' : s === 'viirs' ? 'daynight' : 'truecolor';
/** How a listed scene is NAMED on screen: a VIIRS pass by instant, satellite
 *  and half of the day; anything else by its date. */
function sceneName(d: SceneDate | undefined, key: string): string {
  if (!d?.sat) return key;
  return `${key.slice(5, 10)} ${d.approx ? '≈' : ''}${d.time ?? ''} ${d.satShort ?? d.sat} ${
    d.part === 'night' ? '☾' : '☀'}`;
}
const styleLabelsFor = (s: Source): Record<string, string> =>
  s === 'sar' ? SAR_STYLE_LABELS : isViirs(s) ? VIIRS_STYLE_LABELS : OPTICAL_STYLE_LABELS;
/** An orbit is picked by WHEN it looked as much as by what it saw, so its
 *  label leads with the days it flew — the part a narrow picker still shows. */
const trackLabel = (t: SarTrack): string =>
  `${t.days?.length ? `${fmtDayRun(t.days)} · ` : ''}${
    t.direction === 'ascending' ? 'Ascending' : 'Descending'} ${t.orbit}${
    t.time ? ` · ${t.time} UTC` : ''} · ${Math.round(t.coverage * 100)}% of area`;
const OPTICAL_LAYERS: ChangeLayer[] = ['cva', 'sam', 'dwater', 'dnbr', 'dnbr2', 'bais2',
                                       'hotspot', 'retardant'];
const SAR_LAYERS: ChangeLayer[] = ['sarchange'];

const LAYER_LEGENDS: Record<ChangeLayer, string> = {
  cva: '<i class="grad" style="background:linear-gradient(90deg,#ffff4d,#ff7b00,#e01313)"></i> faint → strong change',
  sam: '<i class="grad" style="background:linear-gradient(90deg,#62b6cb,#f2c14e,#d1495b)"></i> slight → strong material change',
  dnbr: '<i class="sw" style="background:#ffff4d"></i>low <i class="sw" style="background:#ffa61a"></i>mod-low <i class="sw" style="background:#ff4d00"></i>mod-high <i class="sw" style="background:#990000"></i>high severity',
  dnbr2: '<i class="sw" style="background:#ffff4d"></i>light ash <i class="sw" style="background:#ffa61a"></i>moderate <i class="sw" style="background:#ff4d00"></i>heavy char <i class="sw" style="background:#990000"></i>severe',
  bais2: '<i class="grad" style="background:linear-gradient(90deg,#ff8c00,#c32814)"></i> freshly burned surface',
  hotspot: '<i class="sw" style="background:#ff9600"></i>hot <i class="sw" style="background:#ff5a00"></i>hotter <i class="sw" style="background:#fffae6;border:1px solid #ccc"></i>white-hot core (active fire)',
  retardant: '<i class="sw" style="background:#ff60c4"></i>retardant-like <i class="sw" style="background:#b2f7ff"></i>foam-like',
  sarchange: '<i class="grad" style="background:linear-gradient(90deg,#1e5ac8,#96d2f0,#0000,#ffd678,#d12828)"></i> darker (flood/smoothed) ← → brighter (new structure)',
  dwater: '<i class="sw" style="background:#965c28"></i>dried out <i class="sw" style="background:#d6b278"></i>drier <i class="sw" style="background:#8fd0e8"></i>wetter <i class="sw" style="background:#1c50a8"></i>new water',
  anomaly: '<i class="grad" style="background:linear-gradient(90deg,#ffff4d,#ff7b00,#e01313)"></i> just outside → far outside the baseline\'s normal range',
};

function boxPolygon(bbox: [number, number, number, number]): Polygon {
  const [w, s, e, n] = bbox;
  return { type: 'Polygon', coordinates: [[[w, s], [e, s], [e, n], [w, n], [w, s]]] };
}

function coordsFor(bbox: [number, number, number, number]):
    [[number, number], [number, number], [number, number], [number, number]] {
  const [w, s, e, n] = bbox;
  return [[w, n], [e, n], [e, s], [w, s]];
}

let studioOpen = false;

// The Studio keeps its own basemap, separate from the cockpit's: it opens on
// imagery, which is what a scene is read against. Remembered per browser.
const STUDIO_BASEMAP_KEY = 'studio-basemap';
const STUDIO_BASEMAP_DEFAULT = 'esri';

function storedStudioBasemap(): string {
  try {
    const v = localStorage.getItem(STUDIO_BASEMAP_KEY);
    if (v && v in BASEMAP_LABELS) return v;
  } catch { /* storage blocked: the default serves */ }
  return STUDIO_BASEMAP_DEFAULT;
}

// ---------------------------------------------------------------------------

export interface StudioHooks {
  /** A damage marker was recorded here. The main map is a separate MapLibre
   *  instance with its own tiles, so it only learns about it if it is told. */
  onEntityAdded?: (entity: EntityDto) => void;
  /** A sheet was picked here that belongs elsewhere — a map sheet, or another
   *  Studio sheet over its own area. The Studio has already closed. */
  onOpenSheet?: (sheet: SheetDto) => void;
}

export async function openStudio(bbox: [number, number, number, number],
                                 geometry?: Polygon,
                                 place?: { id: string; name?: string | null },
                                 hooks?: StudioHooks,
                                 opts?: { sheet?: SheetDto },
                                 ): Promise<void> {
  if (studioOpen) return;
  studioOpen = true;

  // The area under analysis is STATE, not a fixed opening argument: an operator
  // reading imagery finds the interesting thing just outside the box they drew,
  // and re-opening the Studio to move it loses the whole session. `setScanArea`
  // below is the one place it changes; `openArea` is what Reset goes back to.
  const openArea = geometry ?? boxPolygon(bbox);
  let scanGeometry: Polygon = openArea;
  let scanBbox: [number, number, number, number] = bbox;
  const fmtCoords = (b: [number, number, number, number]): string =>
    `${((b[1] + b[3]) / 2).toFixed(3)}, ${((b[0] + b[2]) / 2).toFixed(3)}`;
  let coordsLabel = fmtCoords(scanBbox);

  // --- shell ---------------------------------------------------------------
  const overlay = document.createElement('div');
  overlay.className = 'studio';
  overlay.innerHTML = `
    <div class="studio-map" id="studio-map"></div>
    <div class="studio-wipe"><span class="grip">⟷</span></div>
    <div class="pod studio-top">
      <span class="coords">${coordsLabel}</span>
      <span class="ab"><b class="tag-a">A</b>
        <select data-ab="a" title="Before scene (or shift-click a date on the strip). Picking B's scene swaps the pair." disabled></select>
        <i>before</i>
        <span class="arr">⟷</span>
        <b class="tag-b">B</b>
        <select data-ab="b" title="After scene (or click a date on the strip). Picking A's scene swaps the pair." disabled></select>
        <i>after</i></span>
      <button class="sbtn" data-act="swap" title="Swap A and B">⇄ Swap</button>
      <span class="series"><span class="xs" data-ab="xs"></span>
        <select data-ab="add" class="add" title="Add a scene to the series (or ctrl-click a date on the strip) — up to ${SERIES_MAX} in all, played in date order by Blink" disabled></select></span>
      <span class="seg">
        <button data-mode="a">A</button>
        <button data-mode="b" class="active">B</button>
        <button data-mode="wipe">Wipe</button>
        <button data-mode="blink">Blink</button>
      </span>
      <span class="imgctl">
        <button class="sbtn active" data-act="img"
          title="Show or hide the satellite imagery">▦ Imagery</button>
        <input type="range" data-act="imgop" min="0" max="100" step="5" value="100"
          title="Imagery opacity — fade the scene toward the basemap" />
      </span>
      <button class="sbtn active" data-act="chg"
        title="Show or hide the change-evidence layer">Δ Change</button>
      <button class="sbtn" data-act="area"
        title="Redraw the area this Studio analyses — drag a new box on the map, as on the main map">▣ Redraw area</button>
      <button class="sbtn load" data-act="load" title="Fetch imagery from Copernicus">▶ Load imagery</button>
      <button class="sbtn" data-act="export" title="Export a still or an animated GIF — the sheet's export">↓ Export</button>
      ${place ? `<button class="sbtn" data-act="boundary"
        title="Redraw this place's boundary on the imagery">▱ Boundary</button>` : ''}
      <button class="sbtn x" data-act="close" title="Close (Esc)">✕</button>
    </div>
    <div class="pod studio-blink" style="display:none">
      <button class="sbtn" data-act="play">⏸</button>
      <button class="sbtn" data-act="prev" title="Previous scene (pauses)">‹</button>
      <button class="sbtn" data-act="next" title="Next scene (pauses)">›</button>
      <label>Speed <input type="range" data-act="speed" min="1" max="10" step="1" value="3" /></label>
      <span class="bl-now" data-act="now"></span>
    </div>
    ${place ? `<div class="pod studio-blink studio-boundary" style="display:none">
      <span class="bnd-name"></span>
      <button class="sbtn" data-bnd="polygon" title="Click the corners, then Finish">Polygon</button>
      <button class="sbtn" data-bnd="box" title="Click two opposite corners">Box</button>
      <button class="sbtn" data-bnd="cancel">Cancel</button>
    </div>` : ''}
    <div class="pod studio-blink studio-area" style="display:none">
      <span class="ar-lbl">Redraw area</span>
      <span class="ar-hint">Drag a box on the map — or</span>
      <button class="sbtn" data-ar="polygon" title="Click the corners, then Finish">Polygon</button>
      <button class="sbtn" data-ar="view" title="Use exactly what is on screen">This view</button>
      <button class="sbtn" data-ar="reset" title="Back to the area this Studio opened with">Reset</button>
      <button class="sbtn" data-ar="cancel" title="Keep the area (Esc)">Cancel</button>
    </div>
    <div class="pod studio-wipesel" style="display:none">
      <select data-wp="left">
        <option value="a" selected>A · before</option>
        <option value="b">B · after</option>
        <option value="map">Basemap</option>
      </select>
      <span class="arr">⟷</span>
      <select data-wp="right">
        <option value="a">A · before</option>
        <option value="b" selected>B · after</option>
        <option value="map">Basemap</option>
      </select>
    </div>
    <div class="pod studio-rail">
      <button data-act="markup" title="Markup — arrows, callouts, pins and shapes, saved to this sheet">✎</button>
      <button data-act="sheet" title="Sheet — title, frame, overlays and export">▤</button>
      <span class="rail-div"></span>
      <button class="dmg" data-dmg="point"
        title="Record damage here — saved for everyone, dated by the B scene">✚</button>
      <button class="dmg" data-dmg="area"
        title="Record a damaged area — saved for everyone, dated by the B scene">⬠</button>
    </div>
    <div class="pod studio-strip">
      <div class="strip-load"></div>
      <div class="axis"></div>
      <div class="hint">Listing the scenes over this area…</div>
    </div>
    <div class="pod studio-analyzer">
      <header data-act="an-toggle">Analyzer <span class="tw">▾</span></header>
      <div class="an-body">
        <div class="an-sec first">Scenes</div>
        <label class="an-slider">Cloud
          <input type="range" data-an="maxcloud" min="5" max="100" step="5" value="40" />
          <span class="val" data-an="cloudval">40%</span></label>
        <label class="an-field">Look back
          <select data-an="months">
            <option value="3">3 months</option>
            <option value="6" selected>6 months</option>
            <option value="12">12 months</option>
            <option value="24">24 months</option>
          </select></label>

        <div class="an-sec">Imagery</div>
        <label class="an-field">Source
          <select data-an="source">
            <option value="optical" selected>Sentinel-2 · optical</option>
            <option value="sar">Sentinel-1 · SAR</option>
            <option value="viirs">VIIRS · NOAA-20/21 + NPP, every pass</option>
          </select></label>
        <label class="an-field" style="display:none" title="Sentinel-1 sees the ground from a different side and angle on each orbit, so a pair only compares like with like when both passes flew the same one. Every scene listed is a pass of this orbit.">Orbit
          <select data-an="track"></select></label>
        <div class="an-note" style="display:none" data-an="tracknote"></div>
        <div class="an-note" style="display:none" data-an="rawnote">Every VIIRS pass over the
          area — three satellites, each once by day and once by night, so about six
          looks a day. ←/→ steps through them. Night is the Day/Night Band: lights,
          flares, fleets and moonlit cloud. Thermal is surface heat (purple cool →
          white hot). Shown as NASA publishes them, so no change layer; FIRMS
          hotspots under Reference layers marks detected fires.</div>
        <label class="an-field">Style
          <select data-an="style">${Object.entries(STYLE_LABELS).map(([k, v]) =>
            `<option value="${k}">${v}</option>`).join('')}</select></label>
        <div class="an-note an-moved">Show and opacity are in the top bar.</div>

        <div class="an-group" data-an="analysis">
        <div class="an-sec">Change evidence</div>
        <label class="an-field">Layer
          <select data-an="layer">${(Object.keys(LAYER_LABELS) as ChangeLayer[]).map((k) =>
            `<option value="${k}">${LAYER_LABELS[k]}</option>`).join('')}</select></label>
        <label class="an-slider">Sensitivity
          <input type="range" data-an="sensitivity" min="1" max="10" step="1" value="5" />
          <span class="val" data-an="sval">5</span></label>
        <div class="an-inline">
          <label class="an-field">Bands
            <select data-an="bandset">
              <option value="extended" selected>Extended · 10</option>
              <option value="core">Core · 6</option>
            </select></label>
          <label class="an-check"><input type="checkbox" data-an="despeckle" /> Despeckle</label>
        </div>
        <label class="an-check" title="Drop change explained by flooding, pools or rain-damp ground — the surfaces that mimic damage. Applies to the difference, spectral-angle and baseline layers.">
          <input type="checkbox" data-an="igwater" /> Ignore water &amp; wet ground</label>
        <label class="an-check" title="Drop change where vegetation or a burn scar RECOVERED — greening is change, but never damage. Applies to the difference, spectral-angle and baseline layers.">
          <input type="checkbox" data-an="igrecov" /> Ignore greening &amp; recovery</label>
        <label class="an-check" title="Burn layers only: flag NOT burned → burned, and skip ground that already read as char in the before scene.">
          <input type="checkbox" data-an="freshburn" /> Fresh burns only</label>
        <div class="an-legend" data-an="legend"></div>

        <div class="an-sec">Scene prep</div>
        <label class="an-check"><input type="checkbox" data-an="register" /> Align scenes
          <span class="an-note" data-an="regstatus"></span></label>
        <label class="an-check"><input type="checkbox" data-an="normalize" /> Match lighting &amp; haze</label>
        <label class="an-slider" title="How much of the lighting correction to apply. Back it off when a broad real change (a big burn, a flood) is being 'corrected' away as if it were haze.">Strength
          <input type="range" data-an="normstr" min="10" max="100" step="5" value="100" disabled />
          <span class="val" data-an="normstrval">100%</span></label>
        <label class="an-check"><input type="checkbox" data-an="isolate" /> Freeze unchanged</label>
        <label class="an-slider" title="How far below the visible change threshold the real after image still shows through. Raise it when freezing erases faint real change; 0 freezes everything under the threshold.">Keep faint
          <input type="range" data-an="keepfaint" min="0" max="80" step="5" value="40" disabled />
          <span class="val" data-an="keepfaintval">40%</span></label>

        <div class="an-sec">Deep analysis</div>
        <div class="an-note">Stacks several scenes up to A into a baseline, then flags
          where B leaves the range those scenes span — variation the ground shows
          anyway (water, weather, season, sun) stays quiet.</div>
        <label class="an-field">Baseline
          <select data-an="deepdepth">
            <option value="3">3 scenes</option>
            <option value="4" selected>4 scenes</option>
            <option value="6">6 scenes</option>
            <option value="8">8 scenes</option>
          </select></label>
        <button class="sbtn" data-an="deeprun" title="Fetches the extra scenes, aligns and matches them, and builds the per-pixel baseline — this takes a moment">▶ Build baseline &amp; compare</button>
        <div class="an-note" data-an="deepstatus"></div>
        </div>

        <div class="an-sec">Adjust</div>
        <label class="an-slider">Smooth
          <input type="range" data-an="blur" min="0" max="4" step="0.5" value="0" /></label>
        <label class="an-slider">Contrast
          <input type="range" data-an="contrast" min="0.5" max="2" step="0.05" value="1" /></label>
        <label class="an-slider">Bright
          <input type="range" data-an="bright" min="0.5" max="2" step="0.05" value="1" /></label>
        <label class="an-slider">Saturate
          <input type="range" data-an="saturate" min="0" max="3" step="0.05" value="1" /></label>
        <label class="an-check"><input type="checkbox" data-an="sharpen" /> Sharpen edges</label>

        <div class="an-sec">Reference layers</div>
        <label class="an-field">Basemap
          <select data-an="basemap">${Object.entries(BASEMAP_LABELS).map(([k, v]) =>
            `<option value="${k}">${v}</option>`).join('')}</select></label>
        <label class="an-check"><input type="checkbox" data-ov="firms" /> FIRMS hotspots</label>
        <label class="an-check"><input type="checkbox" data-ov="objects" /> Map objects</label>
        <label class="an-check"><input type="checkbox" data-ov="damage" /> Damage already recorded</label>
        <label class="an-check"><input type="checkbox" data-ov="live" /> Live events</label>
        <label class="an-check"><input type="checkbox" data-ov="adm" /> Admin boundaries</label>
      </div>
    </div>`;
  document.body.appendChild(overlay);

  const q = <T extends HTMLElement>(sel: string) => overlay.querySelector(sel) as T;
  const mapEl = q<HTMLDivElement>('.studio-map');
  const wipeEl = q<HTMLDivElement>('.studio-wipe');
  const blinkPod = q<HTMLDivElement>('.studio-blink');
  const wipeSelPod = q<HTMLDivElement>('.studio-wipesel');
  const strip = q<HTMLDivElement>('.studio-strip');
  const axis = q<HTMLDivElement>('.axis');
  const hintEl = q<HTMLDivElement>('.studio-strip .hint');
  const coordsEl = q<HTMLSpanElement>('.studio-top .coords');
  const abA = q<HTMLSelectElement>('[data-ab="a"]');
  const abB = q<HTMLSelectElement>('[data-ab="b"]');
  const abXs = q<HTMLSpanElement>('[data-ab="xs"]');
  const abAdd = q<HTMLSelectElement>('[data-ab="add"]');
  const blinkNowEl = q<HTMLSpanElement>('[data-act="now"]');
  const segEl = q<HTMLSpanElement>('.studio-top .seg');
  const wipeSelLeft = q<HTMLSelectElement>('[data-wp="left"]');
  // The top bar wraps to as many rows as its width and the series need; the
  // pods that hang under it (blink, wipe, markup, area) read where it ENDS
  // from --st-bottom rather than assuming one row.
  const topBar = q<HTMLDivElement>('.studio-top');
  new ResizeObserver(() => {
    overlay.style.setProperty('--st-bottom', `${topBar.offsetTop + topBar.offsetHeight}px`);
  }).observe(topBar);
  const wipeSelRight = q<HTMLSelectElement>('[data-wp="right"]');
  const analyzerPod = q<HTMLDivElement>('.studio-analyzer');
  const legendEl = q<HTMLDivElement>('[data-an="legend"]');
  const basemapSel = q<HTMLSelectElement>('[data-an="basemap"]');
  basemapSel.value = storedStudioBasemap();
  const regStatusEl = q<HTMLElement>('[data-an="regstatus"]');

  // --- map -----------------------------------------------------------------
  // The style starts empty: the basemap is the cockpit's set of layers
  // (basemaps.ts), installed on load under everything else.
  const map = new maplibregl.Map({
    container: mapEl,
    style: {
      version: 8,
      glyphs: 'https://tiles.openfreemap.org/fonts/{fontstack}/{range}.pbf',
      sources: {},
      layers: [],
    },
    attributionControl: { compact: true },
    canvasContextAttributes: { preserveDrawingBuffer: true },  // for export
    maxZoom: 20,
    // Pin the 5.x overzoom behaviour — same reasoning as the cockpit map.
    zoomLevelsToOverscale: undefined,
  });
  map.fitBounds([[scanBbox[0], scanBbox[1]], [scanBbox[2], scanBbox[3]]],
                { padding: 90, duration: 0 });
  const mapReady = new Promise<void>((resolve) => map.once('load', () => resolve()));
  (window as { __studio?: unknown }).__studio = map;

  // --- state ---------------------------------------------------------------
  let dates: SceneDate[] = [];        // ascending
  let track: string | null = null;    // SAR: the relative orbit every listed pass shares
  let tracks: SarTrack[] = [];
  let sceneEpoch = 0;                 // bumped whenever the scene set changes identity
  const nameOf = (key: string | null): string =>
    key ? sceneName(dates.find((d) => d.date === key), key) : '—';
  let datesToken = 0;
  let dateA: string | null = null;   // before
  let dateB: string | null = null;   // after
  // THE SERIES. A and B are the PAIR every change layer, freeze and baseline
  // compares; the scenes added beside them are for looking — Blink plays the
  // whole series in date order, the view buttons and each wipe side can show
  // any of it, and export animates it. An added scene is aligned and
  // lighting-matched onto A like B is, so a flip from one to the next shows
  // the ground changing, not the sun. Added scenes are lettered C, D… in date
  // order; A and B keep their letters wherever they fall.
  let extras: string[] = [];         // ascending, never A or B
  let viewX: string | null = null;   // the added scene mode 'x' shows
  const seriesDates = (): string[] =>
    [dateA, dateB, ...extras].filter((d): d is string => !!d).sort();
  const letterOf = (d: string): string =>
    d === dateA ? 'A' : d === dateB ? 'B' : String.fromCharCode(67 + extras.indexOf(d));
  const paneOf = (d: string): Pane => (d === dateA ? 'a' : d === dateB ? 'b' : `x:${d}`);
  const dateOfPane = (p: Pane): string | null =>
    (p === 'a' ? dateA : p === 'b' ? dateB : p.startsWith('x:') ? p.slice(2) : null);
  let maxCloud = 40;                  // scene filter: max cloud cover %
  let monthsBack = 6;                 // scene filter: how far back to list
  let daysBack = 7;                   // ... in days for VIIRS, which images daily
  let mode: Mode = 'b';
  let wipeLeft: Pane = 'a';
  let wipeRight: Pane = 'b';
  let wipeFrac = 0.55;
  let blinkTimer: number | null = null;
  let blinkPlaying = true;
  let blinkFps = 3;
  let blinkIndex = -1;                          // which frame of the series is up
  let blinkFront: 'before' | 'after' = 'after'; // the layer showing it
  let sceneBbox: [number, number, number, number] | null = null;
  let layersReady = false;
  let loadToken = 0;
  let closed = false;
  let loaded = false;   // Copernicus is only hit once the operator presses Load

  // Analyzer state (old-app defaults, except scene prep and despeckle: off
  // until asked for, so the first look is the raw pair).
  let source: Source = 'optical';
  let style = 'truecolor';
  let showImagery = true;
  let imgOpacity = 1;
  let showChange = true;
  let changeLayer: ChangeLayer = 'cva';
  let sensitivity = 5;
  let bandSet: 'core' | 'extended' = 'extended';
  let despeckle = false;
  let register = false;
  let normalize = false;
  let normStrength = 1;      // fraction of the lighting fit applied (scaleFits)
  let isolate = false;
  let keepFaint = 0.4;       // freeze-unchanged: how far below threshold B survives
  let ignoreWater = false;
  let ignoreRecovery = false;
  let freshOnly = false;     // burn layers: not-burned -> burned transitions only
  let blur = 0, contrast = 1, bright = 1, saturate = 1;
  let sharpen = false;

  // Deep analysis: a baseline of several scenes ending at A, judged against B.
  // Everything here is derived from a specific (pair, area, depth) run, so any
  // date/area/source change drops it whole rather than risking a stale stack.
  interface DeepState {
    stats: BaselineStats;
    stack: RawScene;          // per-band medians, rendered as the A pane
    after: RawScene;          // B aligned onto the reference (A) at run time
    fitsAfter: BandFits;
    baseDates: string[];      // ascending, ending at dateA
    id: number;               // memo-key term: a new run invalidates everything
  }
  let deep: DeepState | null = null;
  let deepDepth = 4;
  let deepToken = 0;
  let deepRunSeq = 0;

  const rawCache = new Map<string, RawScene>();               // date -> decoded bands
  const alignedCache = new Map<string, { scene: RawScene; shift: SubpixelShift }>();
  let composedMemo: { key: string; a: HTMLCanvasElement; b: HTMLCanvasElement } | null = null;
  let changeMemo: { key: string; canvas: HTMLCanvasElement | null } | null = null;
  let scoreMemo: { key: string; score: Float32Array | null } | null = null;
  let freezeMemo: { key: string; mask: Uint8Array | null } | null = null;

  type LayerKey = 'before' | 'after' | 'change';
  const display: Record<LayerKey, HTMLCanvasElement> = {
    before: document.createElement('canvas'),
    after: document.createElement('canvas'),
    change: document.createElement('canvas'),
  };

  // --- scene layers (canvas sources) ---------------------------------------
  function ensureLayers(): void {
    if (layersReady || !sceneBbox) return;
    for (const key of ['before', 'after', 'change'] as const) {
      map.addSource(`scene-${key}`, {
        type: 'canvas',
        canvas: display[key],
        coordinates: coordsFor(sceneBbox),
        animate: false,
      } as never);
      map.addLayer({
        id: `scene-${key}`, type: 'raster', source: `scene-${key}`,
        // Both zeros matter: fade-duration kills the tile-load fade, and the
        // opacity TRANSITION kills MapLibre's default 300 ms ease on
        // setPaintProperty — with it, every blink flip crossfaded A and B
        // through partial opacity and the basemap flashed through.
        paint: { 'raster-opacity': 0, 'raster-fade-duration': 0,
                 'raster-opacity-transition': { duration: 0, delay: 0 },
                 ...(key === 'change' ? {} : { 'raster-resampling': sceneResampling() }) },
      });
    }
    layersReady = true;
    raiseOverlays();   // keep reference overlays above the freshly-added scenes
  }

  /** Smooth at 0 means RAW PIXELS: nearest-neighbour, so a 10 m Sentinel-2
   *  pixel zoomed in is a crisp square. MapLibre's raster default is linear,
   *  which blended every pixel into its neighbours at any Smooth setting — the
   *  legacy app's rule (public/app.js applyFilters: `pixelated` at 0) lost in
   *  the port. Any Smooth above 0 goes back to linear so the blur reads right.
   *  The change layer keeps linear, as it always did. */
  function sceneResampling(): 'nearest' | 'linear' {
    return blur > 0 ? 'linear' : 'nearest';
  }
  function syncResampling(): void {
    for (const key of ['before', 'after'] as const) {
      if (!map.getLayer(`scene-${key}`)) continue;
      if (map.getPaintProperty(`scene-${key}`, 'raster-resampling') === sceneResampling()) continue;
      map.setPaintProperty(`scene-${key}`, 'raster-resampling', sceneResampling());
    }
  }

  /** The canvas sources are georeferenced by the corners handed to addSource,
   *  so a re-scan over a NEW area has to move them — otherwise the fresh scene
   *  paints itself onto the old footprint and every pixel is in the wrong
   *  place while looking perfectly plausible. */
  function syncSceneCoords(): void {
    if (!layersReady || !sceneBbox) return;
    const corners = coordsFor(sceneBbox);
    for (const key of ['before', 'after', 'change'] as const) {
      const src = map.getSource(`scene-${key}`) as maplibregl.CanvasSource | undefined;
      src?.setCoordinates(corners);
    }
  }

  function sceneFilter(): string {
    const parts: string[] = [];
    if (blur > 0) parts.push(`blur(${blur}px)`);
    if (contrast !== 1) parts.push(`contrast(${contrast})`);
    if (bright !== 1) parts.push(`brightness(${bright})`);
    if (saturate !== 1) parts.push(`saturate(${saturate})`);
    return parts.join(' ');
  }

  /** Paint `source` into a display canvas, restricted to fraction band [x0,x1). */
  function paintScene(key: LayerKey, source: HTMLCanvasElement | null,
                      x0 = 0, x1 = 1): void {
    const disp = display[key];
    if (source && (disp.width !== source.width || disp.height !== source.height)) {
      disp.width = source.width;
      disp.height = source.height;
    }
    const ctx = disp.getContext('2d')!;
    ctx.clearRect(0, 0, disp.width, disp.height);
    if (source) {
      const sx = Math.round(x0 * disp.width);
      const sw = Math.round((x1 - x0) * disp.width);
      if (sw > 0) {
        ctx.save();
        if (key !== 'change') {
          const f = sceneFilter();
          if (f) ctx.filter = f;
        }
        ctx.drawImage(source, sx, 0, sw, disp.height, sx, 0, sw, disp.height);
        ctx.restore();
      }
    }
    const src = map.getSource(`scene-${key}`) as
      { play?: () => void; pause?: () => void } | undefined;
    src?.play?.();
    src?.pause?.();
  }

  function setOpacity(key: LayerKey, v: number): void {
    if (map.getLayer(`scene-${key}`)) {
      map.setPaintProperty(`scene-${key}`, 'raster-opacity', v);
    }
  }

  function stopBlink(): void {
    if (blinkTimer !== null) {
      clearInterval(blinkTimer);
      blinkTimer = null;
    }
  }

  /** The series as frames, in date order, each with the canvas it shows and
   *  the words that name it. A scene still loading is left out. */
  function seriesFrames(): { date: string; label: string; canvas: HTMLCanvasElement }[] {
    const c = composedCanvases();
    if (!c) return [];
    const out: { date: string; label: string; canvas: HTMLCanvasElement }[] = [];
    for (const d of seriesDates()) {
      const canvas = paneCanvas(paneOf(d), c);
      if (!canvas) continue;
      const label = d === dateA && deep ? `A stack ×${deep.baseDates.length}`
        : `${letterOf(d)} ${nameOf(d)}`;
      out.push({ date: d, label, canvas });
    }
    return out;
  }

  /** Put frame k up. It is painted into whichever of the two scene layers is
   *  HIDDEN and then the two swap, so a series of any length flips through
   *  two layers without ever showing a half-painted one. */
  function showBlinkFrame(frames: ReturnType<typeof seriesFrames>, k: number): void {
    const f = frames[k];
    if (!f) return;
    const back = blinkFront === 'after' ? 'before' : 'after';
    paintScene(back, f.canvas);
    setOpacity(back, showImagery ? imgOpacity : 0);
    setOpacity(blinkFront, 0);
    blinkFront = back;
    blinkIndex = k;
    blinkNowEl.textContent = f.label;
  }

  function startBlink(): void {
    stopBlink();
    const frames = seriesFrames();
    if (!frames.length) return;
    if (blinkIndex < 0 || blinkIndex >= frames.length) {
      blinkIndex = Math.max(0, frames.findIndex((f) => f.date === dateB));
    }
    showBlinkFrame(frames, blinkIndex);
    blinkTimer = window.setInterval(() => {
      showBlinkFrame(frames, (blinkIndex + 1) % frames.length);
    }, 1000 / blinkFps);
  }

  /** Step the flipbook by hand: it pauses, the way a scrubbed video does. */
  function stepBlink(delta: number): void {
    if (mode !== 'blink') return;
    if (blinkPlaying) {
      blinkPlaying = false;
      playBtn.textContent = '▶';
      stopBlink();
    }
    const frames = seriesFrames();
    if (!frames.length) return;
    const k = ((blinkIndex < 0 ? 0 : blinkIndex) + delta + frames.length) % frames.length;
    showBlinkFrame(frames, k);
  }

  // --- compute pipeline ----------------------------------------------------
  function effectiveScenes(): { rawA: RawScene; rawB: RawScene } | null {
    if (!dateA || !dateB) return null;
    const rawA = rawCache.get(dateA);
    const rawB0 = rawCache.get(dateB);
    if (!rawA || !rawB0) return null;
    if (!register || isViirs(source)) {
      regStatusEl.textContent = '';
      return { rawA, rawB: rawB0 };
    }
    const key = `${dateA}|${dateB}`;
    let aligned = alignedCache.get(key);
    if (!aligned) {
      const shift = estimateSubpixelShift(rawA, rawB0);
      const scene = applySubpixelShift(rawB0, shift.dx, shift.dy);
      aligned = { scene, shift };
      alignedCache.set(key, aligned);
    }
    const sh = aligned.shift;
    regStatusEl.textContent = (sh.dx === 0 && sh.dy === 0)
      ? '· no shift needed'
      : `· ${sh.dx > 0 ? '+' : ''}${sh.dx}, ${sh.dy > 0 ? '+' : ''}${sh.dy} px, −${Math.round(sh.improvement * 100)}% residual`;
    return { rawA, rawB: aligned.scene };
  }

  /** The pair every compute path reads: the deep stack + its pre-aligned B
   *  while deep analysis is active, else the plain A/B pair. */
  function activeScenes(): { rawA: RawScene; rawB: RawScene } | null {
    if (deep) return { rawA: deep.stack, rawB: deep.after };
    return effectiveScenes();
  }

  function changeOpts() {
    return { bandSet, sensitivity, normalize, normStrength, despeckle,
             ignoreWater, ignoreRecovery };
  }

  // The freeze mask comes from a score-bearing layer: CVA or SAM (optical), the
  // SAR log-ratio, or the baseline anomaly when deep analysis is active; the
  // other layers don't drive freeze, so they collapse onto the default.
  function freezeLayer(): ChangeLayer {
    if (source === 'sar') return 'sarchange';
    if (changeLayer === 'cva' || changeLayer === 'sam') return changeLayer;
    if (deep) return 'anomaly';
    return 'cva';
  }

  function scoreKey(): string {
    return [source, deep?.id ?? 0, dateA, dateB, freezeLayer(), sensitivity, bandSet,
            normalize, normStrength, despeckle, register, ignoreWater, ignoreRecovery]
      .join('|');
  }

  function scoreOf(layer: ChangeLayer, scenes: { rawA: RawScene; rawB: RawScene }):
      ChangeResult | null {
    if (layer === 'sarchange') return computeSarChange(scenes.rawA, scenes.rawB, changeOpts());
    if (layer === 'anomaly') {
      return deep
        ? computeBaselineAnomaly(deep.stats, deep.after, deep.fitsAfter, changeOpts())
        : null;
    }
    if (layer === 'sam') return computeSpectralAngleChange(scenes.rawA, scenes.rawB, changeOpts());
    return computeChange(scenes.rawA, scenes.rawB, changeOpts());
  }

  function currentScore(scenes: { rawA: RawScene; rawB: RawScene }): Float32Array | null {
    const key = scoreKey();
    if (scoreMemo && scoreMemo.key === key) return scoreMemo.score;
    const result = scoreOf(freezeLayer(), scenes);
    scoreMemo = { key, score: result?.score ?? null };
    return scoreMemo.score;
  }

  function currentFreezeMask(scenes: { rawA: RawScene; rawB: RawScene }): Uint8Array | null {
    const key = `${scoreKey()}·${keepFaint}`;
    if (freezeMemo && freezeMemo.key === key) return freezeMemo.mask;
    const score = currentScore(scenes);
    const mask = score
      ? freezeKeepMask(score, scenes.rawA.width, scenes.rawA.height, { keepFaint })
      : null;
    freezeMemo = { key, mask };
    return mask;
  }

  function composedCanvases(): { a: HTMLCanvasElement; b: HTMLCanvasElement } | null {
    const scenes = activeScenes();
    if (!scenes) return null;
    const raw = isViirs(source);
    const key = [source, deep?.id ?? 0, dateA, dateB, style, normalize, normStrength, register,
                 isolate, sharpen, isolate && !raw ? `${scoreKey()}·${keepFaint}` : '']
      .join('§');
    if (composedMemo && composedMemo.key === key) {
      return { a: composedMemo.a, b: composedMemo.b };
    }
    const pair = raw
      ? { before: compositeRgb(scenes.rawA), after: compositeRgb(scenes.rawB) }
      : compositeScenePair(scenes.rawA, scenes.rawB, style, { normalize, normStrength });
    let beforeImg = pair.before;
    let afterImg = pair.after;
    if (isolate && !raw) {
      const mask = currentFreezeMask(scenes);
      if (mask) afterImg = mergeFrozen(beforeImg, afterImg, mask);
    }
    if (sharpen) {
      beforeImg = sharpenImage(beforeImg);
      afterImg = sharpenImage(afterImg);
    }
    composedMemo = { key, a: imageDataToCanvas(beforeImg), b: imageDataToCanvas(afterImg) };
    return { a: composedMemo.a, b: composedMemo.b };
  }

  function changeCanvas(): HTMLCanvasElement | null {
    const scenes = activeScenes();
    if (!scenes || isViirs(source)) return null;
    const key = [source, deep?.id ?? 0, dateA, dateB, changeLayer, sensitivity, bandSet,
                 normalize, normStrength, despeckle, register, ignoreWater,
                 ignoreRecovery, freshOnly].join('§');
    if (changeMemo && changeMemo.key === key) return changeMemo.canvas;
    let img: ImageData | null = null;
    if (source === 'sar' || changeLayer === 'cva' || changeLayer === 'sam'
        || changeLayer === 'anomaly') {
      // Score-bearing layers: keep the score for freeze so it isn't computed
      // twice. The scoreKey layer term matches by construction (sar collapses
      // onto sarchange; cva/sam/anomaly are themselves).
      const r = scoreOf(source === 'sar' ? 'sarchange' : changeLayer, scenes);
      img = r?.imageData ?? null;
      if (r) scoreMemo = { key: scoreKey(), score: r.score };
    } else {
      switch (changeLayer) {
        case 'dwater':
          img = compositeWaterChange(scenes.rawA, scenes.rawB, { normalize, normStrength });
          break;
        case 'dnbr': img = compositeDNBR(scenes.rawA, scenes.rawB, { freshOnly }); break;
        case 'dnbr2': img = compositeDNBR2(scenes.rawA, scenes.rawB, { freshOnly }); break;
        case 'bais2': img = compositeBAIS2(scenes.rawA, scenes.rawB, { freshOnly }); break;
        case 'hotspot': img = compositeHotspot(scenes.rawB); break;
        case 'retardant':
          img = compositeRetardant(scenes.rawA, scenes.rawB, { normalize, normStrength });
          break;
      }
    }
    changeMemo = { key, canvas: img ? imageDataToCanvas(img) : null };
    return changeMemo.canvas;
  }

  function paneCanvas(pane: Pane, c: { a: HTMLCanvasElement; b: HTMLCanvasElement }):
      HTMLCanvasElement | null {
    if (pane === 'a') return c.a;
    if (pane === 'b') return c.b;
    return pane.startsWith('x:') ? extraCanvas(pane.slice(2)) : null;
  }

  /** An added scene, rendered the way B is: aligned onto A, lighting-matched
   *  and tone-mapped against A, with unchanged ground frozen to A's pixels by
   *  its own change score against A — so flipping A → C → B moves only what
   *  moved. Under deep analysis it is still matched to the real A scene: the
   *  stack is a median, not an acquisition to register against. */
  const extraMemo = new Map<string, { key: string; canvas: HTMLCanvasElement | null }>();
  function extraCanvas(date: string): HTMLCanvasElement | null {
    const rawA = dateA ? rawCache.get(dateA) : undefined;
    const rawX0 = rawCache.get(date);
    if (!rawA || !rawX0 || !dateA) return null;
    const raw = isViirs(source);
    // The anomaly layer scores B against the baseline; an added scene takes
    // the plain multispectral difference instead.
    const layer: ChangeLayer = freezeLayer() === 'anomaly' ? 'cva' : freezeLayer();
    const key = [source, dateA, style, normalize, normStrength, register, sharpen,
                 isolate && !raw ? [layer, sensitivity, bandSet, despeckle, ignoreWater,
                                    ignoreRecovery, keepFaint].join('|') : ''].join('§');
    const hit = extraMemo.get(date);
    if (hit && hit.key === key) return hit.canvas;
    const rawX = register && !raw ? alignOnto(dateA, rawA, date) : rawX0;
    let img: ImageData;
    if (raw) {
      img = compositeRgb(rawX);
    } else {
      const pair = compositeScenePair(rawA, rawX, style, { normalize, normStrength });
      img = pair.after;
      if (isolate) {
        const score = scoreOf(layer, { rawA, rawB: rawX })?.score;
        if (score) {
          img = mergeFrozen(pair.before, img,
                            freezeKeepMask(score, rawA.width, rawA.height, { keepFaint }));
        }
      }
    }
    if (sharpen) img = sharpenImage(img);
    const canvas = imageDataToCanvas(img);
    extraMemo.set(date, { key, canvas });
    return canvas;
  }

  function applyMode(): void {
    stopBlink();
    blinkPod.style.display = mode === 'blink' ? 'flex' : 'none';
    wipeSelPod.style.display = mode === 'wipe' ? 'flex' : 'none';
    wipeEl.classList.toggle('show', mode === 'wipe');
    const canvases = composedCanvases();
    if (!canvases || !layersReady) return;
    const base = showImagery ? imgOpacity : 0;
    const chg = showChange ? changeCanvas() : null;

    if (mode === 'a') {
      paintScene('before', canvases.a);
      setOpacity('before', base);
      setOpacity('after', 0);
      if (chg) paintScene('change', chg);
      setOpacity('change', chg ? 1 : 0);
    } else if (mode === 'b') {
      paintScene('after', canvases.b);
      setOpacity('before', 0);
      setOpacity('after', base);
      if (chg) paintScene('change', chg);
      setOpacity('change', chg ? 1 : 0);
    } else if (mode === 'x') {
      // One added scene on its own. The change layer stays: it is the A/B
      // pair's evidence, and seeing it over C says where to look.
      const cx = viewX ? extraCanvas(viewX) : null;
      paintScene('after', cx);
      setOpacity('before', 0);
      setOpacity('after', cx ? base : 0);
      if (chg) paintScene('change', chg);
      setOpacity('change', chg ? 1 : 0);
    } else if (mode === 'blink') {
      // The whole series in date order — a flip between two scenes, or a
      // time-lapse through six.
      if (chg) paintScene('change', chg);
      setOpacity('change', chg ? 1 : 0);
      if (blinkPlaying) startBlink();
      else {
        const frames = seriesFrames();
        if (blinkIndex < 0 || blinkIndex >= frames.length) {
          blinkIndex = Math.max(0, frames.findIndex((f) => f.date === dateB));
        }
        showBlinkFrame(frames, blinkIndex);
      }
    } else { // wipe: left pane in the 'before' layer, right pane in 'after'
      paintScene('before', paneCanvas(wipeLeft, canvases), 0, wipeFrac);
      paintScene('after', paneCanvas(wipeRight, canvases), wipeFrac, 1);
      setOpacity('before', wipeLeft === 'map' ? 0 : base);
      setOpacity('after', wipeRight === 'map' ? 0 : base);
      // The change layer follows the divider onto the right pane (old-app rule).
      if (chg) paintScene('change', chg, wipeFrac, 1);
      setOpacity('change', chg ? 1 : 0);
      positionWipe();
    }
    renderLegend();
  }

  function renderLegend(): void {
    const on = showChange && !isViirs(source);
    legendEl.innerHTML = on ? LAYER_LEGENDS[changeLayer] : '';
    legendEl.style.display = on ? '' : 'none';
  }

  // Heavy recompute path: yields a frame so the loading bar paints first.
  let recompToken = 0;
  function recompute(): void {
    const token = ++recompToken;
    setLoading(true);
    window.setTimeout(() => {
      if (token !== recompToken || closed) return;
      try {
        applyMode();
      } finally {
        if (token === recompToken && !closed) setLoading(false);
      }
    }, 30);
  }

  // Cheap path for slider drags: repaint only (memos already hot unless the
  // slider invalidates them — recompute() is used for those).
  let paintTimer: number | null = null;
  function schedulePaint(): void {
    if (paintTimer !== null) clearTimeout(paintTimer);
    paintTimer = window.setTimeout(() => {
      paintTimer = null;
      applyMode();
    }, 60);
  }

  // --- loading -------------------------------------------------------------
  function setLoading(on: boolean): void {
    strip.classList.toggle('loading', on);
  }

  /** Fetch whatever of the series the cache is missing — the pair, and any
   *  scenes added to it — in ONE scan, so every scene lands on one grid. */
  async function loadPair(): Promise<void> {
    if (!dateA || !dateB) return;
    const token = ++loadToken;
    const epoch = sceneEpoch;
    const [dA, dB] = [dateA, dateB];
    // B first: index 0 is the after scene in the scan contract.
    const members = [dB, dA, ...extras];
    setLoading(true);
    try {
      const need = members.filter((d) => !rawCache.has(d));
      if (!need.length && !sceneBbox) need.push(dB);
      if (need.length) {
        const scan = await post<ScanResponse>('/api/scan', {
          geometry: scanGeometry,
          dates: need,
          maxDimension: 1400,
          source,
          // A SAR series is passes of ONE orbit; the server resolves each
          // date to that orbit's pass and renders only it.
          ...(source === 'sar' && track ? { track } : {}),
          // VIIRS: the passes themselves (satellite, GIBS day, half of the day).
          ...(isViirs(source) ? {
            view: style,
            passes: need.map((k) => {
              const d = dates.find((x) => x.date === k);
              return { sat: d?.sat, day: d?.day, part: d?.part };
            }),
          } : {}),
        });
        const wanted = need.map((d, i): [string, number] => [d, i]);
        const empty: string[] = [];
        await Promise.all(wanted.map(async ([date, i]) => {
          if (rawCache.has(date)) return;
          const res = await fetch(`/api/scan/${scan.scanId}/scene/${i}.tiff`);
          if (!res.ok) throw new Error('Scene fetch failed.');
          const buf = await res.arrayBuffer();
          const scene = scan.format === 'png'
            ? await decodeImageBands(buf, scan.noDataBlack?.[i] ?? true)
            : await decodeAllBands(buf, scan.bandOrder);
          // The orbit, source or area changed while this was in flight: these
          // pixels belong to a scene set the cache no longer describes.
          if (epoch !== sceneEpoch) return;
          if (scan.format === 'png' && validShare(scene) < 0.001) empty.push(date);
          rawCache.set(date, scene);
        }));
        if (epoch !== sceneEpoch) return;
        if (empty.length) {
          toast(`No image over this area for ${empty.sort().map(nameOf).join(' and ')
          } yet — NASA publishes a pass a few hours after it flies.`);
        }
        sceneBbox = scan.bbox;
      }
      if (token !== loadToken || closed) return;
      await mapReady;
      if (token !== loadToken || closed) return;
      ensureLayers();
      syncSceneCoords();   // the area may have moved since these layers were added
      recompute();
    } catch (err) {
      if (token === loadToken && !closed) {
        toast(err instanceof Error ? err.message : 'Scene load failed.');
      }
    } finally {
      if (token === loadToken && !closed) setLoading(false);
    }
  }

  // List the scenes for this area under the current cloud/look-back filter.
  // `reselect` picks the newest two (boot, or when the filter change drops the
  // current pair); otherwise a still-available A/B is preserved so tweaking the
  // cloud limit doesn't yank the comparison out from under you.
  //
  // This is a CATALOGUE query and nothing else — it costs no processing units,
  // so it runs on open and on every filter change, loaded or not. Choosing the
  // pair is the whole point of the filmstrip, and it has to be possible BEFORE
  // paying for the scenes: only `loadPair` (below) fetches pixels, and it is
  // reached from the Load button.
  async function loadDates(reselect: boolean): Promise<void> {
    const tok = ++datesToken;
    setLoading(true);
    let res: DatesResponse;
    try {
      res = await post<DatesResponse>('/api/dates', {
        geometry: scanGeometry, maxCloud, monthsBack, source,
        ...(isViirs(source) ? { daysBack, view: style } : {}),
        ...(source === 'sar' && track ? { track } : {}),
      });
    } catch (err) {
      if (tok !== datesToken) return;   // a newer listing owns the strip
      if (!closed) {
        setLoading(false);
        axis.innerHTML = `<div class="empty">${
          err instanceof Error ? err.message : 'Date lookup failed.'}</div>`;
        chipEls.clear();     // the dots those entries pointed at are gone
        dates = [];
        syncPairSelects();   // and so are the dropdowns' scenes
        syncLoadCta();       // Load becomes the retry
      }
      return;
    }
    if (closed || tok !== datesToken) return;
    if (source === 'sar') {
      const prevTrack = track;
      tracks = res.tracks ?? [];
      track = res.track ?? null;
      // The server fell back to another orbit (the old one doesn't fly over
      // this look-back): same dates can mean different passes now.
      if (track !== prevTrack) dropScenes();
    }
    renderTrackPicker();
    const prevA = dateA, prevB = dateB;
    dates = res.dates.slice().sort((x, y) => x.date.localeCompare(y.date));
    const have = new Set(dates.map((d) => d.date));
    extras = reselect ? [] : extras.filter((d) => have.has(d));
    if (reselect || !dateA || !dateB || !have.has(dateA) || !have.has(dateB)) {
      dateB = res.dates[0]?.date ?? null;   // /api/dates is newest-first
      // A VIIRS pass is compared with the same half of the PREVIOUS day, not
      // with the day, nor with another satellite twenty minutes earlier.
      const b = res.dates[0];
      const dayBefore = b?.part ? res.dates.slice(1).find((d) => d.part === b.part
        && Date.parse(b.date) - Date.parse(d.date) >= 12 * 3600_000) : undefined;
      dateA = (dayBefore ?? res.dates[1])?.date ?? null;
      extras = extras.filter((d) => d !== dateA && d !== dateB);
    }
    if (deep && (dateA !== prevA || dateB !== prevB)) deactivateDeep(true);
    buildStrip();
    refreshStrip();
    renderLegend();
    if (loaded && dateA && dateB) await loadPair();   // owns the indicator from here
    else setLoading(false);
  }

  // --- filmstrip -----------------------------------------------------------
  // Scenes are small dots on a time axis. Labels appear on hover, on the A/B
  // marked scenes, and on <=6 evenly-spaced anchor ticks for orientation.
  // VIIRS passes ride one LANE per satellite under day rules instead: three
  // satellites cross within the hour, so on one line a day's passes were one
  // unclickable blob (strip.ts has the arithmetic).
  const chipEls = new Map<string, HTMLElement>();

  function buildStrip(): void {
    axis.innerHTML = '';
    axis.classList.remove('lanes');
    axis.style.removeProperty('--lanes');
    chipEls.clear();
    if (dates.length < 2) {
      axis.innerHTML = '<div class="empty">Fewer than two scenes here — raise the cloud limit or look back further.</div>';
      return;
    }
    const t0 = Date.parse(dates[0]!.date);
    const t1 = Date.parse(dates[dates.length - 1]!.date);
    const span = t1 - t0;
    const lanes = laneKeys(dates);
    // Lanes leave a left gutter for the satellite names.
    const [x0, xw] = lanes.length ? [8, 90] : [4, 92];
    const xOf = (ts: number): number => x0 + (span > 0 ? (ts - t0) / span : 0.5) * xw;
    const axisW = axis.clientWidth || 700;
    const add = (cls: string, left: number | null, text = ''): HTMLDivElement => {
      const el = document.createElement('div');
      el.className = cls;
      if (left != null) el.style.left = `${left}%`;
      el.textContent = text;
      axis.appendChild(el);
      return el;
    };
    if (lanes.length) {
      axis.classList.add('lanes');
      axis.style.setProperty('--lanes', String(lanes.length));
      const marks = dayMarks(t0, t1, (axisW * xw) / 100);
      for (const m of marks.rules) add('day-rule', xOf(m));
      for (const l of marks.labels) add('anchor', xOf(l.at), l.text);
      lanes.forEach((sat, i) => {
        const first = dates.find((d) => d.sat === sat);
        const label = add('lane-label', null, first?.satShort ?? sat);
        label.title = first?.satName ?? sat;
        label.style.setProperty('--lane', String(i));
        add('lane-rule', null).style.setProperty('--lane', String(i));
      });
    } else if (span > 0) {
      // Anchor ticks: at most 6, evenly spaced along the axis, month/day only.
      for (let i = 0; i < 6; i++) {
        const f = i / 5;
        add('anchor', x0 + f * xw, fmtMonthDay(t0 + f * span));
      }
    }
    // Scene dots, nudged vertically when they crowd so none are hidden (a
    // lane already keeps a satellite's passes twelve hours apart).
    let prevX = -Infinity;
    let flip = false;
    for (const d of dates) {
      const xPct = xOf(Date.parse(d.date));
      const xPx = (xPct / 100) * axisW;
      let nudge = 0;
      if (!lanes.length && xPx - prevX < 7) {
        flip = !flip;
        nudge = flip ? -3 : 3;
      } else {
        flip = false;
      }
      prevX = xPx;
      const dot = document.createElement('div');
      dot.className = d.part === 'night' ? 'dot night' : 'dot';
      dot.style.left = `${xPct}%`;
      if (nudge) dot.style.setProperty('--nudge', `${nudge}px`);
      if (lanes.length) dot.style.setProperty('--lane', String(Math.max(0, lanes.indexOf(d.sat ?? ''))));
      dot.innerHTML = `<span class="pt"></span>
        <span class="tip">${d.sat ? `${sceneName(d, d.date)} · ${d.satName}`
          : `${d.date.slice(5)}${d.cloud != null ? ` · ${d.cloud}%` : ''}${
          d.time ? ` · ${d.time}Z` : ''}${d.coverage != null && d.coverage < 0.95
          ? ` · ${Math.round(d.coverage * 100)}% of area` : ''}`}</span>
        <span class="mark"></span>`;
      dot.addEventListener('click', (ev) => {
        // Ctrl (⌘ on a Mac, or Alt) adds the scene to the series, or takes it out.
        if (ev.ctrlKey || ev.metaKey || ev.altKey) toggleExtra(d.date);
        else if (ev.shiftKey) setPair(d.date, dateB);
        else setPair(dateA, d.date);
      });
      axis.appendChild(dot);
      chipEls.set(d.date, dot);
    }
  }

  function refreshStrip(): void {
    for (const [date, dot] of chipEls) {
      const isA = date === dateA;
      const isB = date === dateB;
      const isX = extras.includes(date);
      dot.classList.toggle('is-a', isA);
      dot.classList.toggle('is-b', isB);
      dot.classList.toggle('is-x', isX);
      const mark = dot.querySelector('.mark') as HTMLElement;
      if (isA || isB || isX) {
        const info = dates.find((d) => d.date === date);
        const cloud = info?.cloud != null ? ` · ${info.cloud}%` : '';
        const letter = letterOf(date);
        mark.textContent = info?.sat ? `${letter} ${sceneName(info, date)}`
          : `${letter} ${date.slice(5)}${cloud}`;
      } else {
        mark.textContent = '';
      }
    }
    placeMarks();
    syncPairSelects();
    syncSeries();
    syncLoadCta();
  }

  // --- the series beyond the pair --------------------------------------------
  function toggleExtra(date: string): void {
    if (extras.includes(date)) removeExtra(date);
    else addExtra(date);
  }

  function addExtra(date: string): void {
    if (date === dateA || date === dateB || extras.includes(date)) return;
    if (seriesDates().length >= SERIES_MAX) {
      toast(`A series holds ${SERIES_MAX} scenes — take one out first.`);
      return;
    }
    extras = [...extras, date].sort();
    seriesChanged();
  }

  function removeExtra(date: string): void {
    if (!extras.includes(date)) return;
    extras = extras.filter((d) => d !== date);
    seriesChanged();
  }

  /** The series grew, shrank or re-lettered: every place that names or shows
   *  it follows, and a loaded Studio fetches what it now lacks. */
  function seriesChanged(): void {
    if (mode === 'x' && (!viewX || !extras.includes(viewX))) { mode = 'b'; viewX = null; }
    const valid = (p: Pane): boolean => !p.startsWith('x:') || extras.includes(p.slice(2));
    if (!valid(wipeLeft)) wipeLeft = 'a';
    if (!valid(wipeRight)) wipeRight = 'b';
    blinkIndex = -1;       // the frames were renumbered; start again at B
    refreshStrip();
    if (loaded) void loadPair();   // fetches only what the cache lacks, then repaints
  }

  /** The chips, the add picker, the view buttons and the wipe pickers. */
  function syncSeries(): void {
    // A wipe side or a view on an added scene that left the added ones —
    // taken out, or picked as A or B — follows it there, or falls back.
    const settle = (p: Pane, fallback: Pane): Pane => {
      if (!p.startsWith('x:')) return p;
      const d = p.slice(2);
      return extras.includes(d) ? p : d === dateA ? 'a' : d === dateB ? 'b' : fallback;
    };
    wipeLeft = settle(wipeLeft, 'a');
    wipeRight = settle(wipeRight, 'b');
    if (mode === 'x' && (!viewX || !extras.includes(viewX))) {
      mode = viewX && viewX === dateA ? 'a' : 'b';
      viewX = null;
    }
    // One chip per added scene: its letter and date opens it on its own, ✕
    // takes it out of the series.
    abXs.replaceChildren(...extras.map((d) => {
      const chip = document.createElement('span');
      chip.className = `xchip${mode === 'x' && viewX === d ? ' on' : ''}`;
      chip.innerHTML = `<button type="button" class="xv" title="Show this scene on its own">
          <b class="tag-x"></b> <span></span></button><button type="button" class="xdel"
          title="Take it out of the series">✕</button>`;
      (chip.querySelector('.tag-x') as HTMLElement).textContent = letterOf(d);
      const info = dates.find((x) => x.date === d);
      (chip.querySelector('.xv span') as HTMLElement).textContent =
        info?.sat ? sceneName(info, d) : d.slice(5, 10);
      chip.querySelector('.xv')!.addEventListener('click', () => setView('x', d));
      chip.querySelector('.xdel')!.addEventListener('click', () => removeExtra(d));
      return chip;
    }));
    const inSeries = new Set(seriesDates());
    const room = inSeries.size < SERIES_MAX;
    abAdd.replaceChildren(new Option(room ? '＋ Scene' : 'Series full', ''),
      ...dates.slice().reverse().filter((d) => !inSeries.has(d.date))
        .map((d) => new Option(optionLabel(d), d.date)));
    abAdd.value = '';
    abAdd.disabled = !room || dates.length < 3;
    // A view button per added scene, between B and the two-scene modes.
    segEl.querySelectorAll('[data-mode="x"]').forEach((b) => b.remove());
    const wipeBtn = segEl.querySelector('[data-mode="wipe"]');
    for (const d of extras) {
      const b = document.createElement('button');
      b.dataset.mode = 'x';
      b.dataset.x = d;
      b.textContent = letterOf(d);
      b.title = `${letterOf(d)} ${nameOf(d)} on its own`;
      segEl.insertBefore(b, wipeBtn);
    }
    segEl.querySelectorAll<HTMLButtonElement>('button').forEach((b) => {
      b.classList.toggle('active', b.dataset.mode === mode
        && (mode !== 'x' || b.dataset.x === viewX));
    });
    // Each wipe side can be any scene of the series, or the basemap.
    for (const [sel, pane] of [[wipeSelLeft, wipeLeft], [wipeSelRight, wipeRight]] as const) {
      sel.replaceChildren(...seriesDates().map((d) =>
        new Option(`${letterOf(d)} · ${nameOf(d)}`, paneOf(d))), new Option('Basemap', 'map'));
      sel.value = pane;
    }
    refreshSheetFrames(map);
  }

  function setView(next: Mode, x: string | null = null): void {
    mode = next;
    viewX = next === 'x' ? x : null;
    syncSeries();
    applyMode();
  }

  abAdd.addEventListener('change', () => {
    if (abAdd.value) addExtra(abAdd.value);
    abAdd.value = '';
  });

  // The same pair, picked from a list: the strip is a timeline, and a date
  // is sometimes easier to find by name — or impossible to hit as a dot, when
  // a long look-back packs a hundred scenes into the strip's width.
  let listedFor: SceneDate[] | null = null;
  // Short, like the strip's own marks: the top bar has two of these to fit.
  const optionLabel = (d: SceneDate): string => {
    if (d.sat) return sceneName(d, d.date);
    const day = d.date.slice(0, 10);
    if (d.time || d.coverage != null) {
      return `${day}${d.time ? ` ${d.time}Z` : ''}${d.coverage != null && d.coverage < 0.95
        ? ` · ${Math.round(d.coverage * 100)}% area` : ''}`;
    }
    return `${day}${d.cloud != null ? ` ☁ ${d.cloud}%` : ''}`;
  };
  function syncPairSelects(): void {
    // The options only change with the listing; newest first, like the strip
    // read from its live end.
    if (listedFor !== dates) {
      listedFor = dates;
      for (const sel of [abA, abB]) {
        sel.replaceChildren(...dates.slice().reverse().map((d) => new Option(optionLabel(d), d.date)));
      }
    }
    const usable = dates.length >= 2;
    for (const [sel, mine, other, tag] of [[abA, dateA, dateB, 'B'], [abB, dateB, dateA, 'A']] as const) {
      sel.disabled = !usable;
      // The other side's scene is still choosable — picking it swaps the pair,
      // as clicking the other marker on the strip does — and says so.
      for (const opt of Array.from(sel.options)) {
        if (!opt.value) continue;
        const d = dates.find((x) => x.date === opt.value);
        const text = d ? optionLabel(d) : opt.value;
        opt.textContent = opt.value === other ? `${text} (${tag})` : text;
      }
      sel.querySelector('option[data-stack]')?.remove();
      if (!usable || !mine) {
        if (!sel.querySelector('option[value=""]')) sel.prepend(new Option('—', ''));
        sel.value = '';
        continue;
      }
      sel.querySelector('option[value=""]')?.remove();
      sel.value = mine;
    }
    // While deep analysis is on, the A pane is the stack, and the picker must
    // say so — "A 2026-06-14" over a median of five dates would be a lie.
    // Choosing a date from it leaves the baseline, as setPair does anyway.
    if (deep) {
      const stack = new Option(`stack ×${deep.baseDates.length}`, '');
      stack.dataset.stack = '1';
      stack.disabled = true;
      abA.prepend(stack);
      abA.value = '';
      stack.selected = true;
    }
  }
  abA.addEventListener('change', () => {
    // A over the stack is the baseline's own A: choosing it is leaving the
    // stack for that scene, which setPair sees as no change at all.
    if (deep && abA.value === dateA) deactivateDeep();
    else setPair(abA.value, dateB);
    syncPairSelects();
  });
  abB.addEventListener('change', () => { setPair(dateA, abB.value); syncPairSelects(); });

  // The A and B labels are centred on their dots unless that runs them off
  // the strip (B is the newest scene by default, at the very end) or over each
  // other; then they slide, each still over (at worst ending at) its own dot.
  // Two passes of one VIIRS cluster are too close for two names: then each
  // label is its letter, flagged beside its own stem, and the top bar carries
  // the names.
  function placeMarks(): void {
    const axisW = axis.clientWidth;
    const marks = [dateA, dateB, ...extras]
      .map((k) => (k ? chipEls.get(k) : undefined))
      .filter((el): el is HTMLElement => !!el)
      .map((el) => ({ el: el.querySelector('.mark') as HTMLElement,
                      x: (parseFloat(el.style.left) / 100) * axisW }))
      .sort((p, q) => p.x - q.x);
    for (const m of marks) {
      m.el.style.removeProperty('transform');
      m.el.style.removeProperty('--stem');
    }
    const measure = () => marks.map((m) => ({ x: m.x, w: m.el.offsetWidth }));
    let boxes = measure();
    if (!axisW || !boxes.length || boxes.some((b) => !b.w)) return;   // not laid out
    let { left, fits } = placeLabels(boxes, axisW, 7);
    if (!fits) ({ left, fits } = placeLabels(boxes, axisW, 0));   // edge on the dot
    if (!fits) {
      for (const m of marks) m.el.textContent = (m.el.textContent ?? '').slice(0, 1);
      boxes = measure();
      ({ left } = placeLabels(boxes, axisW, 0));
    }
    marks.forEach((m, i) => {
      m.el.style.transform = `translateX(${Math.round(left[i]! - m.x)}px)`;
      m.el.style.setProperty('--stem', `${Math.round(m.x - left[i]!)}px`);
    });
  }

  // Before the imagery is fetched the strip is a PICKER and the bar has to say
  // so: the pair is chosen first and paid for second. Afterwards the hint goes
  // back to the keyboard shortcuts, which only mean anything once loaded.
  function syncLoadCta(): void {
    loadBtn.classList.toggle('ready', !loaded && !!dateA && !!dateB);
    hintEl.textContent = loaded
      ? 'Click a date to set B · shift-click sets A · ctrl-click adds it to the series · ←/→ move B · space plays blink'
      : dates.length
        ? 'Click a date to set B · shift-click sets A · ctrl-click adds more (or pick in the top bar) — then press Load imagery'
        : '';
  }

  function setPair(a: string | null, b: string | null): void {
    if (!a || !b) return;
    if (a === b) { const t = dateA; a = dateB; b = t; }  // picked the other marker's date: swap
    if (!a || !b || a === b) return;
    if (a === dateA && b === dateB) return;
    dateA = a;
    dateB = b;
    // A scene picked as A or B leaves the added ones: it has its letter now.
    if (extras.includes(a) || extras.includes(b)) {
      extras = extras.filter((d) => d !== a && d !== b);
      if (mode === 'x' && viewX && !extras.includes(viewX)) { mode = 'b'; viewX = null; }
    }
    blinkIndex = -1;
    deactivateDeep(true);   // the baseline was built for the OLD pair
    refreshStrip();
    if (loaded) void loadPair();   // pre-Load, picking a pair costs nothing
  }

  function moveB(delta: number): void {
    if (!dateB) return;
    let i = dates.findIndex((d) => d.date === dateB);
    if (i < 0) return;
    i += delta;
    // Hop over A and the added scenes: stepping B must not eat the series.
    const taken = new Set([dateA, ...extras]);
    while (dates[i] && taken.has(dates[i]!.date)) i += delta;
    const next = dates[i];
    if (next) setPair(dateA, next.date);
  }

  // --- wipe divider --------------------------------------------------------
  function wipeEdges(): { xw: number; xe: number } | null {
    const bb = sceneBbox ?? scanBbox;
    const cy = (bb[1] + bb[3]) / 2;
    return { xw: map.project([bb[0], cy]).x, xe: map.project([bb[2], cy]).x };
  }

  function positionWipe(): void {
    const edges = wipeEdges();
    if (!edges) return;
    wipeEl.style.left = `${edges.xw + wipeFrac * (edges.xe - edges.xw)}px`;
  }

  wipeEl.addEventListener('pointerdown', (ev) => {
    ev.preventDefault();
    wipeEl.setPointerCapture(ev.pointerId);
    const onMove = (mv: PointerEvent) => {
      const edges = wipeEdges();
      if (!edges || edges.xe === edges.xw) return;
      wipeFrac = Math.min(1, Math.max(0, (mv.clientX - edges.xw) / (edges.xe - edges.xw)));
      positionWipe();
      if (mode === 'wipe' && layersReady) {
        const canvases = composedCanvases();
        if (canvases) {
          paintScene('before', paneCanvas(wipeLeft, canvases), 0, wipeFrac);
          paintScene('after', paneCanvas(wipeRight, canvases), wipeFrac, 1);
          if (showChange) {
            const chg = changeCanvas();
            if (chg) paintScene('change', chg, wipeFrac, 1);
          }
        }
      }
    };
    const onUp = () => {
      wipeEl.removeEventListener('pointermove', onMove);
      wipeEl.removeEventListener('pointerup', onUp);
    };
    wipeEl.addEventListener('pointermove', onMove);
    wipeEl.addEventListener('pointerup', onUp);
  });
  map.on('move', () => { if (mode === 'wipe') positionWipe(); });

  overlay.querySelectorAll<HTMLSelectElement>('[data-wp]').forEach((sel) =>
    sel.addEventListener('change', () => {
      if (sel.dataset.wp === 'left') wipeLeft = sel.value as Pane;
      else wipeRight = sel.value as Pane;
      applyMode();
    }));

  // --- markup and the sheet -------------------------------------------------
  //
  // The Studio's markup is the cockpit's engine (markup.ts): MapLibre layers on
  // this map, saved to a sheet, so a note survives the Studio closing, comes
  // back when the sheet is reopened, restyles and moves, and exports with the
  // imagery under it. It replaced a 2D canvas that drew red screen shapes and
  // died with the session.
  //
  // The sheet (sheets.ts) is this Studio's composition: a frame, the overlays,
  // the imagery pair, a title and a caption. It stays a draft — written
  // nowhere — until something is drawn on it or Save is pressed, so opening
  // the Studio to look at a scene leaves nothing behind.

  function openMarkupHere(): void {
    if (isMarkupActive(map)) return;
    if (damageTool) setDamageTool(null);    // annotation and recording are exclusive
    openMarkup(map, {
      mount: overlay,
      // The Studio IS the editing surface: its rail (damage tools, markup
      // toggle), strip and close stay live while the bar is up.
      extraControls: () => [overlay],
      sheetLabel: () => activeSheetTitle(map),
      onSheet: () => toggleSheetPod(map),
      sheetOpen: () => isSheetPodOpen(map),
      onExport: () => { void exportActiveSheet(map); },
      onClose: syncRailButtons,
    });
    syncRailButtons();
  }
  function syncRailButtons(): void {
    overlay.querySelector('[data-act="markup"]')?.classList.toggle('active', isMarkupActive(map));
  }
  q<HTMLButtonElement>('[data-act="markup"]').addEventListener('click', () => {
    if (isMarkupActive(map)) closeMarkup(map);
    else openMarkupHere();
    syncRailButtons();
  });
  // The sheet flyout hangs off the markup bar (sheets.ts arms it), so this
  // button and the bar's own Sheet button are the same toggle.
  q<HTMLButtonElement>('[data-act="sheet"]').addEventListener('click', () => toggleSheetPod(map));

  // --- export frames ---------------------------------------------------------
  //
  // A blink GIF is the single most useful artefact this tool makes: a still of
  // a damaged building persuades nobody, while the same building flicking
  // between two dates is self-evident. The sheet's export (sheets.ts,
  // exporter.ts) asks this Studio to STAGE each frame — A, B or the basemap —
  // through the same opacity switches the blink mode uses, then reads the
  // canvas back. The change overlay, reference overlays and markup are left as
  // they are, so an export is what is on screen.

  // A frame is a Pane: A, B, an added scene of the series, or the basemap.
  type ExportFrame = Pane;

  const captionFor = (frame: ExportFrame | null): string => {
    const layerNote = showChange && !isViirs(source) ? ` · ${LAYER_LABELS[changeLayer]}` : '';
    const styleName = styleLabelsFor(source)[style] ?? style;
    const platform = source === 'sar'
      ? `Sentinel-1 SAR${track ? ` (orbit ${track})` : ''} via Copernicus`
      : isViirs(source) ? 'VIIRS via NASA GIBS'
      : 'Sentinel-2 via Copernicus';
    // A blink with no per-frame label is unreadable — the viewer cannot tell
    // which way round it is, which is the one thing the comparison must say.
    // Under deep analysis the A frame is a median of several dates, and a
    // caption naming one date over a stack would be provenance fiction.
    const aLabel = deep
      ? `A stack ×${deep.baseDates.length} (${deep.baseDates[0]}→${dateA ?? '—'}) · baseline`
      : `A ${nameOf(dateA)} · before`;
    const xDate = frame?.startsWith('x:') ? frame.slice(2) : null;
    const lead = frame === 'a' ? aLabel
      : frame === 'b' ? `B ${nameOf(dateB)} · after`
      : xDate ? `${letterOf(xDate)} ${nameOf(xDate)} · ${seriesDates().indexOf(xDate) + 1} of ${
        seriesDates().length}`
      : frame === 'map' ? `Basemap · ${BASEMAP_LABELS[basemapSel.value] ?? basemapSel.value} · reference`
      : `${deep ? `Baseline ×${deep.baseDates.length}` : `A ${nameOf(dateA)}`} vs B ${nameOf(dateB)}`;
    return `${lead} · ${styleName}${layerNote} · ${coordsLabel} · ${platform}`;
  };

  /** Put the map into the state a single export frame should capture. */
  async function stageFrame(frame: ExportFrame): Promise<void> {
    const canvases = composedCanvases();
    const base = showImagery ? imgOpacity : 0;
    // A in the before layer, everything else of the series in the after one.
    if (canvases && layersReady) {
      if (frame === 'a') paintScene('before', canvases.a);
      else if (frame !== 'map') paintScene('after', paneCanvas(frame, canvases));
    }
    setOpacity('before', frame === 'a' ? base : 0);
    setOpacity('after', frame !== 'a' && frame !== 'map' ? base : 0);
    // The change overlay is evidence about the PAIR, not about one date, so it
    // would be a lie stamped on the basemap frame.
    setOpacity('change', frame !== 'map' && showChange ? 1 : 0);
    await mapSettled(map);
  }

  let staged: { mode: Mode; playing: boolean } | null = null;
  const sheetHost: SheetHost = {
    surface: 'studio',
    map,
    mount: overlay,
    // The series in date order — an animation is a time-lapse — then the
    // basemap. Before anything is listed, A and B are still offered.
    frames: () => [
      ...(seriesDates().length ? seriesDates().map((d) => ({
        key: paneOf(d),
        label: d === dateA ? 'A before' : d === dateB ? 'B after' : `${letterOf(d)} ${d.slice(5, 10)}`,
        on: true,
      })) : [{ key: 'a', label: 'A before', on: true }, { key: 'b', label: 'B after', on: true }]),
      { key: 'map', label: 'Basemap', on: false },
    ],
    timeFrames: false,
    async stage(key) {
      if (!key) return;
      if (!staged) {
        staged = { mode, playing: blinkPlaying };
        stopBlink();
      }
      await stageFrame(key as ExportFrame);
    },
    async unstage() {
      if (!staged) return;
      mode = staged.mode;
      blinkPlaying = staged.playing;
      staged = null;
      applyMode();      // always put the operator's view back
    },
    footer: (key) => captionFor((key as ExportFrame | null) ?? null),
    legend: () => (loaded && showChange
      ? [{ label: `Change evidence · ${LAYER_LABELS[changeLayer]}`, color: '#ff3b30' }] : []),
    captureLayers: () => ({
      basemap: basemapSel.value,
      overlays: Object.fromEntries([...overlay.querySelectorAll<HTMLInputElement>('[data-ov]')]
        .map((cb) => [cb.dataset.ov!, cb.checked])),
      showImagery, imgOpacity, showChange,
    }),
    applyLayers(layers) {
      // A sheet's basemap is shown, not remembered: it is that sheet's choice,
      // and the next Studio opens on the operator's own.
      if (typeof layers.basemap === 'string' && layers.basemap in BASEMAP_LABELS
          && layers.basemap !== basemapSel.value) {
        basemapSel.value = layers.basemap;
        setBasemap(map, basemapSel.value);
      }
      const want = layers.overlays as Record<string, unknown> | undefined;
      if (!want) return;
      overlay.querySelectorAll<HTMLInputElement>('[data-ov]').forEach((cb) => {
        const on = want[cb.dataset.ov!];
        if (typeof on === 'boolean' && cb.checked !== on) {
          cb.checked = on;
          cb.dispatchEvent(new Event('change'));
        }
      });
    },
    captureImagery: () => ({ area: scanGeometry, dateA, dateB, source, style, changeLayer,
                             ...(extras.length ? { series: extras } : {}),
                             ...(source === 'sar' && track ? { track } : {}) }),
    wholeArea: () => sceneBbox ?? scanBbox,
    wholeAreaLabel: 'Image',
    openElsewhere: (picked) => {
      close();
      hooks?.onOpenSheet?.(picked);
    },
    startMarkup: () => openMarkupHere(),
    defaultTitle: () => `Imagery · ${coordsLabel}${dateB ? ` · ${dateB}` : ''}`,
  };

  /** A saved sheet's imagery settings, applied BEFORE the scene list is
   *  fetched: the source decides which collection is listed at all. Nothing
   *  has been decoded yet, so there is no cache for the switch to invalidate. */
  function applySheetImagerySettings(im: Record<string, unknown> | null): void {
    if (!im) return;
    // 'noaa20'/'noaa21' were single-satellite sources for one day (2026-09-26).
    const wanted = im.source === 'noaa20' || im.source === 'noaa21' ? 'viirs' : im.source;
    const known: Source[] = ['optical', 'sar', 'viirs'];
    if (known.includes(wanted as Source) && wanted !== source) {
      source = wanted as Source;
      style = defaultStyle(source);
      changeLayer = source === 'sar' ? 'sarchange' : 'cva';
      rebuildStyleAndLayer();
      toggleSourceUI();
      an<HTMLSelectElement>('source').value = source;
    }
    // The pair was two passes of this orbit; listing another would lose it.
    if (source === 'sar' && typeof im.track === 'string') track = im.track;
    const styleSel = an<HTMLSelectElement>('style');
    if (typeof im.style === 'string' && [...styleSel.options].some((o) => o.value === im.style)) {
      style = im.style;
      styleSel.value = style;
    }
    if (typeof im.changeLayer === 'string' && im.changeLayer in LAYER_LABELS) {
      changeLayer = im.changeLayer as ChangeLayer;
      an<HTMLSelectElement>('layer').value = changeLayer;
    }
  }

  /** And its pair, once the list is in. Picking costs nothing; Load still does. */
  function applySheetPair(im: Record<string, unknown>): void {
    const a = typeof im.dateA === 'string' ? im.dateA : null;
    const b = typeof im.dateB === 'string' ? im.dateB : null;
    if (a && b && dates.some((d) => d.date === a) && dates.some((d) => d.date === b)) {
      setPair(a, b);
      // The scenes added to its series, those still listed.
      const more = Array.isArray(im.series)
        ? im.series.filter((d): d is string => typeof d === 'string'
          && d !== a && d !== b && dates.some((x) => x.date === d)).slice(0, SERIES_MAX - 2)
        : [];
      if (more.length) {
        extras = [...new Set(more)].sort();
        seriesChanged();
      }
      toast(`Sheet opened with A ${a} and B ${b}${more.length ? ` and ${more.length} more` : ''
      } — press ▶ Load imagery to fetch them.`);
    } else if (a || b) {
      toast('The sheet’s pair is not in this scene list — raise the cloud limit or look back further.');
    }
  }

  // --- modes / top bar -----------------------------------------------------
  segEl.addEventListener('click', (ev) => {
    const btn = (ev.target as HTMLElement).closest<HTMLButtonElement>('button[data-mode]');
    if (!btn) return;
    setView(btn.dataset.mode as Mode, btn.dataset.x ?? null);
  });

  const playBtn = q<HTMLButtonElement>('[data-act="play"]');
  function togglePlay(): void {
    blinkPlaying = !blinkPlaying;
    playBtn.textContent = blinkPlaying ? '⏸' : '▶';
    if (mode === 'blink') applyMode();
  }
  playBtn.addEventListener('click', togglePlay);
  q<HTMLButtonElement>('[data-act="prev"]').addEventListener('click', () => stepBlink(-1));
  q<HTMLButtonElement>('[data-act="next"]').addEventListener('click', () => stepBlink(1));
  q<HTMLInputElement>('[data-act="speed"]').addEventListener('input', (ev) => {
    blinkFps = Number((ev.target as HTMLInputElement).value);
    if (mode === 'blink' && blinkPlaying && blinkTimer !== null) startBlink();
  });

  q<HTMLButtonElement>('[data-act="swap"]').addEventListener('click', () => setPair(dateB, dateA));
  q<HTMLButtonElement>('[data-act="export"]').addEventListener('click', () => {
    void openSheetPod(map, 'export');
  });
  q<HTMLButtonElement>('[data-act="close"]').addEventListener('click', close);

  // Manual imagery load. The Studio lists the available scenes on open — a
  // catalogue search, free — so the source, filters and the A/B pair are all
  // chosen before anything is fetched. Only this button spends the quota;
  // after it, date/filter/source changes refetch live.
  const loadBtn = q<HTMLButtonElement>('[data-act="load"]');
  loadBtn.addEventListener('click', () => {
    loaded = true;
    loadBtn.textContent = '⟳ Reload';
    // A date-lookup failure leaves the strip empty; Load has to be able to
    // retry it, or a transient error strands the session with no way back.
    if (!dates.length) void loadDates(true);
    else if (dates.length < 2) {
      toast('Fewer than two scenes here — raise the cloud limit or look back further.');
    } else if (!dateA || !dateB) toast('Pick a before and an after date on the strip first.');
    else void loadPair();
    syncLoadCta();
  });

  // --- analyzer panel ------------------------------------------------------
  // On a phone the analyzer is a bottom sheet (mobile.css) that would otherwise
  // open over most of the imagery. Land on the picture; the header is the grab
  // bar that opens the controls.
  const setAnalyzerCollapsed = (collapsed: boolean) => {
    analyzerPod.classList.toggle('collapsed', collapsed);
    (analyzerPod.querySelector('.tw') as HTMLElement).textContent = collapsed ? '▸' : '▾';
  };
  if (isCompactViewport()) setAnalyzerCollapsed(true);
  q<HTMLElement>('[data-act="an-toggle"]').addEventListener('click', () => {
    setAnalyzerCollapsed(!analyzerPod.classList.contains('collapsed'));
  });

  const an = <T extends HTMLElement>(name: string) =>
    overlay.querySelector(`[data-an="${name}"]`) as T;

  // Scene filters re-list the available dates. The slider updates its label
  // live on `input` but only re-fetches on `change` (release), so dragging it
  // doesn't fire a request per step.
  an<HTMLInputElement>('maxcloud').addEventListener('input', (ev) => {
    an<HTMLElement>('cloudval').textContent = `${(ev.target as HTMLInputElement).value}%`;
  });
  an<HTMLInputElement>('maxcloud').addEventListener('change', (ev) => {
    maxCloud = Number((ev.target as HTMLInputElement).value);
    void loadDates(false);   // re-lists the strip; fetches only once loaded
  });
  an<HTMLSelectElement>('months').addEventListener('change', (ev) => {
    const v = Number((ev.target as HTMLSelectElement).value);
    if (isViirs(source)) daysBack = v; else monthsBack = v;
    void loadDates(false);
  });

  // Source switch (Sentinel-2 optical <-> Sentinel-1 SAR) swaps the whole scene
  // set: different acquisition dates, band bytes, styles and change layer. Reset
  // every scene cache/memo and re-list dates for the new collection.
  function rebuildStyleAndLayer(): void {
    const styleSel = an<HTMLSelectElement>('style');
    const styleLabels = styleLabelsFor(source);
    styleSel.innerHTML = Object.entries(styleLabels).map(([k, v]) =>
      `<option value="${k}">${v}</option>`).join('');
    styleSel.value = style;
    const layerSel = an<HTMLSelectElement>('layer');
    // The anomaly layer only means anything while a baseline exists, so it is
    // offered exactly then — and first, since it is what the run was for.
    const layers = source === 'sar' ? SAR_LAYERS
      : isViirs(source) ? []
      : deep ? (['anomaly', ...OPTICAL_LAYERS] as ChangeLayer[]) : OPTICAL_LAYERS;
    layerSel.innerHTML = layers.map((k) =>
      `<option value="${k}">${LAYER_LABELS[k]}</option>`).join('');
    layerSel.value = changeLayer;
  }
  function toggleSourceUI(): void {
    const optical = source === 'optical';
    // Cloud cover and multispectral band-set are Sentinel-2-only concepts.
    const cloudRow = an<HTMLInputElement>('maxcloud').closest('label') as HTMLElement | null;
    if (cloudRow) cloudRow.style.display = optical ? '' : 'none';
    const bandRow = an<HTMLSelectElement>('bandset').closest('label') as HTMLElement | null;
    if (bandRow) bandRow.style.display = optical ? '' : 'none';
    // A daily source looks back in days; the others in months.
    const back = an<HTMLSelectElement>('months');
    back.innerHTML = (isViirs(source)
      ? [[3, '3 days'], [7, '7 days'], [14, '14 days'], [30, '30 days']]
      : [[3, '3 months'], [6, '6 months'], [12, '12 months'], [24, '24 months']])
      .map(([v, label]) => `<option value="${v}">${label}</option>`).join('');
    back.value = String(isViirs(source) ? daysBack : monthsBack);
    // VIIRS arrives as pictures: nothing to difference, align or stack.
    an<HTMLElement>('analysis').style.display = isViirs(source) ? 'none' : '';
    an<HTMLElement>('rawnote').style.display = isViirs(source) ? '' : 'none';
    renderTrackPicker();
  }
  /** Everything decoded or derived belongs to one scene set (source, orbit,
   *  VIIRS product, area); when that changes it all goes, and a fetch still in
   *  flight lands nowhere. */
  function dropScenes(): void {
    sceneEpoch += 1;
    deactivateDeep(true);
    rawCache.clear();
    alignedCache.clear();
    extraMemo.clear();
    composedMemo = null; changeMemo = null; scoreMemo = null; freezeMemo = null;
  }
  function setSource(next: Source): void {
    if (next === source) return;
    source = next;
    dropScenes();
    dates = []; dateA = null; dateB = null; extras = [];
    track = null; tracks = [];
    style = defaultStyle(next);
    changeLayer = next === 'sar' ? 'sarchange' : 'cva';
    rebuildStyleAndLayer();
    toggleSourceUI();
    void loadDates(true);   // a different collection: a different scene list
  }

  // SAR's orbit picker. The note says what the picker is for when the orbit
  // misses part of the area, because the blank strip is the design: stitching
  // in another orbit's pass is exactly what painted it red and blue.
  function renderTrackPicker(): void {
    const sel = an<HTMLSelectElement>('track');
    const row = sel.closest('label') as HTMLElement;
    const note = an<HTMLElement>('tracknote');
    const on = source === 'sar' && tracks.length > 0;
    row.style.display = on ? '' : 'none';
    note.style.display = on ? '' : 'none';
    if (!on) return;
    // Listed like a timetable, the orbit that flew most recently first: the
    // question is usually "which orbit looked right after the event". The
    // server's order (most ground first) still picks the default.
    const latest = (t: SarTrack): string => t.days?.[0] ?? '';
    sel.innerHTML = tracks.slice().sort((x, y) => latest(y).localeCompare(latest(x)))
      .map((t) => `<option value="${t.id}">${trackLabel(t)}</option>`).join('');
    if (track) sel.value = track;
    const cur = tracks.find((t) => t.id === track);
    // The picker's closed field shows only the start of a label, so the note
    // spells out when the chosen orbit flies.
    const when = cur?.days?.length
      ? `Passes at ${cur.time ?? '—'} UTC; lately ${fmtDayRun(cur.days)}. `
      : '';
    note.textContent = !cur ? ''
      : cur.coverage >= 0.95 ? when.trim()
      : `${when}This orbit sees ${Math.round(cur.coverage * 100)}% of the area. The rest stays blank `
        + 'rather than stitched from another orbit, whose different viewing angle would read as '
        + 'change everywhere — pick another orbit to read that part.';
    note.style.display = note.textContent ? '' : 'none';
  }
  an<HTMLSelectElement>('track').addEventListener('change', (ev) => {
    track = (ev.target as HTMLSelectElement).value;
    dropScenes();
    dates = []; dateA = null; dateB = null; extras = [];
    void loadDates(true);   // another orbit: other passes, other days
  });
  an<HTMLSelectElement>('source').addEventListener('change', (ev) => {
    setSource((ev.target as HTMLSelectElement).value as Source);
  });

  an<HTMLSelectElement>('style').addEventListener('change', (ev) => {
    style = (ev.target as HTMLSelectElement).value;
    if (isViirs(source)) {   // another view: other products, maybe other passes
      dropScenes();
      void loadDates(false);   // keeps A/B where they are still listed; loads if loaded
      return;
    }
    recompute();
  });
  // Imagery on/off and its opacity are the pair you reach for constantly while
  // reading a scene — "is that shape in the picture or only in the basemap" is
  // answered by flicking it off — so they live in the top bar next to the view
  // modes, the same move Δ Change made out of the Analyzer. MOVED, not copied:
  // two controls over one variable drift apart the moment one path forgets to
  // sync. The slider disables with the toggle, because fading something hidden
  // does nothing and a live control that does nothing reads as broken.
  const imgBtn = q<HTMLButtonElement>('[data-act="img"]');
  const imgOpEl = q<HTMLInputElement>('[data-act="imgop"]');
  imgBtn.addEventListener('click', () => {
    showImagery = !showImagery;
    imgBtn.classList.toggle('active', showImagery);
    imgOpEl.disabled = !showImagery;
    applyMode();
  });
  imgOpEl.addEventListener('input', () => {
    imgOpacity = Number(imgOpEl.value) / 100;
    schedulePaint();
  });
  // Top-bar toggle (was an Analyzer checkbox — hiding the change layer is a
  // constant reach while reading imagery, so it lives with the view controls).
  const chgBtn = q<HTMLButtonElement>('[data-act="chg"]');
  chgBtn.addEventListener('click', () => {
    showChange = !showChange;
    chgBtn.classList.toggle('active', showChange);
    recompute();
  });
  an<HTMLSelectElement>('layer').addEventListener('change', (ev) => {
    changeLayer = (ev.target as HTMLSelectElement).value as ChangeLayer;
    recompute();
  });
  an<HTMLInputElement>('sensitivity').addEventListener('input', (ev) => {
    sensitivity = Number((ev.target as HTMLInputElement).value);
    an<HTMLElement>('sval').textContent = String(sensitivity);
    recompute();
  });
  an<HTMLSelectElement>('bandset').addEventListener('change', (ev) => {
    bandSet = (ev.target as HTMLSelectElement).value as 'core' | 'extended';
    recompute();
  });
  an<HTMLInputElement>('despeckle').addEventListener('change', (ev) => {
    despeckle = (ev.target as HTMLInputElement).checked;
    recompute();
  });
  an<HTMLInputElement>('register').addEventListener('change', (ev) => {
    register = (ev.target as HTMLInputElement).checked;
    recompute();
  });
  // Each prep checkbox owns a slider that means nothing while it is off, so the
  // slider disables with it — a live control that does nothing reads as broken.
  an<HTMLInputElement>('normalize').addEventListener('change', (ev) => {
    normalize = (ev.target as HTMLInputElement).checked;
    an<HTMLInputElement>('normstr').disabled = !normalize;
    recompute();
  });
  an<HTMLInputElement>('normstr').addEventListener('change', (ev) => {
    normStrength = Number((ev.target as HTMLInputElement).value) / 100;
    an<HTMLElement>('normstrval').textContent = `${Math.round(normStrength * 100)}%`;
    recompute();
  });
  an<HTMLInputElement>('normstr').addEventListener('input', (ev) => {
    an<HTMLElement>('normstrval').textContent = `${(ev.target as HTMLInputElement).value}%`;
  });
  an<HTMLInputElement>('isolate').addEventListener('change', (ev) => {
    isolate = (ev.target as HTMLInputElement).checked;
    an<HTMLInputElement>('keepfaint').disabled = !isolate;
    recompute();
  });
  an<HTMLInputElement>('keepfaint').addEventListener('change', (ev) => {
    keepFaint = Number((ev.target as HTMLInputElement).value) / 100;
    an<HTMLElement>('keepfaintval').textContent = `${Math.round(keepFaint * 100)}%`;
    recompute();
  });
  an<HTMLInputElement>('keepfaint').addEventListener('input', (ev) => {
    an<HTMLElement>('keepfaintval').textContent = `${(ev.target as HTMLInputElement).value}%`;
  });
  an<HTMLInputElement>('igwater').addEventListener('change', (ev) => {
    ignoreWater = (ev.target as HTMLInputElement).checked;
    recompute();
  });
  an<HTMLInputElement>('igrecov').addEventListener('change', (ev) => {
    ignoreRecovery = (ev.target as HTMLInputElement).checked;
    recompute();
  });
  an<HTMLInputElement>('freshburn').addEventListener('change', (ev) => {
    freshOnly = (ev.target as HTMLInputElement).checked;
    recompute();
  });

  // --- deep analysis --------------------------------------------------------
  //
  // A two-date comparison answers "what differs between these acquisitions";
  // this answers the harder question, "what differs from what this ground
  // NORMALLY looks like". Several scenes ending at A are co-registered and
  // radiometrically fitted onto A, stacked into per-pixel medians and spreads
  // (raster.ts), and B is scored against that envelope — so a pond that comes
  // and goes, a lot that fills on workdays, or seasonal greening sit INSIDE
  // the range and stay quiet, while a fresh scar that no baseline scene ever
  // showed stands out. The A pane becomes the median stack (which also
  // averages away single-scene noise and one-off contamination); every style,
  // view mode, freeze and export path works on it unchanged.
  //
  // Everything heavy yields to the event loop between stages, because the full
  // build is seconds of arithmetic and the page must stay alive under it.

  const deepBtn = an<HTMLButtonElement>('deeprun');
  const deepStatus = an<HTMLElement>('deepstatus');
  const yieldFrame = (): Promise<void> => new Promise((r) => window.setTimeout(r, 0));

  function syncDeepUi(): void {
    deepBtn.textContent = deep ? '✕ Back to two-scene compare' : '▶ Build baseline & compare';
    deepBtn.classList.toggle('active', !!deep);
    if (deep) {
      const n = deep.baseDates.length;
      deepStatus.textContent = `Baseline ×${n}: ${deep.baseDates[0]} → ${
        deep.baseDates[n - 1]}, compared against B ${dateB}. Scenes aligned and `
        + 'lighting-matched internally.';
    } else {
      deepStatus.textContent = '';
    }
  }

  function deactivateDeep(quiet = false): void {
    deepToken += 1;               // strand any build in flight either way
    deepBtn.disabled = false;     // a stranded build's finally no longer runs
    if (!deep) return;
    deep = null;
    if (changeLayer === 'anomaly') changeLayer = 'cva';
    rebuildStyleAndLayer();
    composedMemo = null; changeMemo = null; scoreMemo = null; freezeMemo = null;
    syncDeepUi();
    refreshStrip();               // the A label goes back to being a date
    if (!quiet) recompute();
  }

  /** Align a scene onto the reference through the shared cache, so a pair the
   *  operator already aligned is never aligned twice. */
  function alignOnto(refDate: string, refScene: RawScene, date: string): RawScene {
    const key = `${refDate}|${date}`;
    let hit = alignedCache.get(key);
    if (!hit) {
      const moving = rawCache.get(date)!;
      const shift = estimateSubpixelShift(refScene, moving);
      hit = { scene: applySubpixelShift(moving, shift.dx, shift.dy), shift };
      alignedCache.set(key, hit);
    }
    return hit.scene;
  }

  async function runDeep(): Promise<void> {
    if (source !== 'optical') {
      toast('Deep analysis reads Sentinel-2 scenes — switch Source to Sentinel-2.');
      return;
    }
    if (!loaded || !dateA || !dateB || !rawCache.has(dateA) || !rawCache.has(dateB)) {
      toast('Load an A/B pair first — the baseline builds backwards from A.');
      return;
    }
    // The baseline is every listed scene up to and including A, newest last.
    // B stays out of it by construction: judging B against a range B helped
    // define would blunt exactly the anomaly being looked for.
    const pool = dates.map((d) => d.date).filter((d) => d <= dateA!);
    const baseDates = pool.slice(-deepDepth);
    if (baseDates.length < 3) {
      toast('A baseline needs at least three scenes up to A — look back further or raise the cloud limit.');
      return;
    }
    const token = ++deepToken;
    deepBtn.disabled = true;
    setLoading(true);
    const status = (t: string) => { deepStatus.textContent = t; };
    try {
      // 1. One scan call for whatever the cache is missing. Same geometry and
      // maxDimension as loadPair, so every scene lands on the same pixel grid.
      const missing = baseDates.filter((d) => !rawCache.has(d));
      if (missing.length) {
        status(`Fetching ${missing.length} baseline scene${missing.length > 1 ? 's' : ''} from Copernicus…`);
        const scan = await post<ScanResponse>('/api/scan', {
          geometry: scanGeometry, dates: missing, maxDimension: 1400, source });
        if (token !== deepToken || closed) return;
        await Promise.all(missing.map(async (date, i) => {
          const res = await fetch(`/api/scan/${scan.scanId}/scene/${i}.tiff`);
          if (!res.ok) throw new Error(`Could not fetch the ${date} scene.`);
          rawCache.set(date, await decodeAllBands(await res.arrayBuffer(), scan.bandOrder));
        }));
        if (token !== deepToken || closed) return;
      }
      // 2. Everything aligns and fits onto the reference: A, the newest
      // baseline scene — the pixel grid the operator is already looking at.
      const reference = rawCache.get(dateA!)!;
      const inputs: { raw: RawScene; fits: BandFits | null }[] = [];
      for (const [idx, d] of baseDates.entries()) {
        if (d === dateA) { inputs.push({ raw: reference, fits: null }); continue; }
        status(`Aligning scene ${idx + 1}/${baseDates.length} (${d})…`);
        await yieldFrame();
        if (token !== deepToken || closed) return;
        const aligned = alignOnto(dateA!, reference, d);
        inputs.push({ raw: aligned, fits: radiometricFit(reference, aligned, RAW_BANDS) });
      }
      status(`Aligning B (${dateB})…`);
      await yieldFrame();
      if (token !== deepToken || closed) return;
      const after = alignOnto(dateA!, reference, dateB!);
      const fitsAfter = radiometricFit(reference, after, RAW_BANDS);
      // 3. Per-pixel stats, one band per frame.
      const { count, dataMask } = baselineCounts(inputs);
      const median: Record<string, Uint16Array> = {};
      const spread: Record<string, Float32Array> = {};
      for (const [bi, name] of RAW_BANDS.entries()) {
        status(`Building the baseline… band ${bi + 1}/${RAW_BANDS.length}`);
        await yieldFrame();
        if (token !== deepToken || closed) return;
        const stat = baselineBandStat(inputs, name, dataMask);
        median[name] = stat.median;
        spread[name] = stat.spread;
      }
      const stats: BaselineStats = { width: reference.width, height: reference.height,
                                     count, dataMask, median, spread };
      deep = { stats, stack: baselineScene(stats), after, fitsAfter, baseDates,
               id: ++deepRunSeq };
      changeLayer = 'anomaly';
      rebuildStyleAndLayer();
      composedMemo = null; changeMemo = null; scoreMemo = null; freezeMemo = null;
      syncDeepUi();
      refreshStrip();
      recompute();
      toast(`Baseline of ${baseDates.length} scenes built — flagging what falls outside their normal range.`);
    } catch (err) {
      if (token === deepToken && !closed) {
        toast(err instanceof Error ? err.message : 'Deep analysis failed.');
        status('');
      }
    } finally {
      if (token === deepToken && !closed) {
        deepBtn.disabled = false;
        setLoading(false);
      }
    }
  }

  an<HTMLSelectElement>('deepdepth').addEventListener('change', (ev) => {
    deepDepth = Number((ev.target as HTMLSelectElement).value);
    // A different depth is a different baseline; nothing changes until rebuilt.
    if (deep) deepStatus.textContent = 'Depth changed — leave and rebuild the baseline to apply it.';
  });
  deepBtn.addEventListener('click', () => {
    if (deep) deactivateDeep();
    else void runDeep();
  });
  for (const [name, set] of [
    ['blur', (v: number) => { blur = v; syncResampling(); }],
    ['contrast', (v: number) => { contrast = v; }],
    ['bright', (v: number) => { bright = v; }],
    ['saturate', (v: number) => { saturate = v; }],
  ] as [string, (v: number) => void][]) {
    an<HTMLInputElement>(name).addEventListener('input', (ev) => {
      set(Number((ev.target as HTMLInputElement).value));
      schedulePaint();
    });
  }
  an<HTMLInputElement>('sharpen').addEventListener('change', (ev) => {
    sharpen = (ev.target as HTMLInputElement).checked;
    recompute();
  });

  // --- basemap --------------------------------------------------------------
  basemapSel.addEventListener('change', () => {
    setBasemap(map, basemapSel.value);
    try { localStorage.setItem(STUDIO_BASEMAP_KEY, basemapSel.value); } catch { /* not remembered */ }
  });

  // --- reference overlays (FIRMS, objects, live events, admin) -------------
  // The operational layers, pinned over the imagery for context. Objects and
  // live events share the entities MVT (split by type_key); admin is the adm
  // vector source; FIRMS is fetched for the scene box. All start hidden and
  // ride ABOVE the scene layers (raiseOverlays keeps them there).
  const OV_LAYERS = ['ov-adm0', 'ov-adm1', 'ov-obj-fill', 'ov-obj-outline',
                     'ov-obj-line', 'ov-obj-point', 'ov-live',
                     'ov-dmg-fill', 'ov-dmg-outline', 'ov-dmg-pt', 'ov-firms'];
  const OV_GROUP: Record<string, string[]> = {
    firms: ['ov-firms'], live: ['ov-live'],
    objects: ['ov-obj-fill', 'ov-obj-outline', 'ov-obj-line', 'ov-obj-point'],
    // Damage is its own group, not part of "Map objects": while marking, what
    // matters is what is ALREADY recorded here — a blue dot among every other
    // blue dot is how a strike gets recorded twice.
    damage: ['ov-dmg-fill', 'ov-dmg-outline', 'ov-dmg-pt'],
    adm: ['ov-adm0', 'ov-adm1'],
  };
  let overlaysReady = false;

  function raiseOverlays(): void {
    if (overlaysReady) {
      for (const id of OV_LAYERS) if (map.getLayer(id)) map.moveLayer(id);
    }
    // Markup rides above everything — scenes and overlays alike — as on the
    // cockpit: an annotation of the map is never hidden behind the map.
    raiseMarkupLayers(map);
  }

  async function ensureOverlays(): Promise<void> {
    if (overlaysReady) return;
    await mapReady;
    if (closed || overlaysReady) return;
    map.addSource('ov-ent', { type: 'vector',
      tiles: [`${location.origin}/api/tiles/entities/{z}/{x}/{y}`], minzoom: 0, maxzoom: 18 });
    map.addSource('ov-adm-src', { type: 'vector',
      tiles: [`${location.origin}/api/tiles/adm/{z}/{x}/{y}`], minzoom: 0, maxzoom: 14 });
    map.addSource('ov-firms-src', { type: 'geojson',
      data: { type: 'FeatureCollection', features: [] } });
    const hidden = { visibility: 'none' as const };
    map.addLayer({ id: 'ov-adm0', type: 'line', source: 'ov-adm-src', 'source-layer': 'adm0',
      layout: hidden, paint: { 'line-color': '#eef3f8', 'line-width': 1.4, 'line-opacity': 0.75 } });
    map.addLayer({ id: 'ov-adm1', type: 'line', source: 'ov-adm-src', 'source-layer': 'adm1',
      layout: hidden, paint: { 'line-color': '#c3d0de', 'line-width': 0.7, 'line-dasharray': [3, 2], 'line-opacity': 0.6 } });
    map.addLayer({ id: 'ov-obj-fill', type: 'fill', source: 'ov-ent', 'source-layer': 'polygons',
      layout: hidden, paint: { 'fill-color': '#38bdf8', 'fill-opacity': 0.12 } });
    map.addLayer({ id: 'ov-obj-outline', type: 'line', source: 'ov-ent', 'source-layer': 'polygons',
      layout: hidden, paint: { 'line-color': '#38bdf8', 'line-width': 1.6 } });
    map.addLayer({ id: 'ov-obj-line', type: 'line', source: 'ov-ent', 'source-layer': 'lines',
      layout: hidden, paint: { 'line-color': '#38bdf8', 'line-width': 1.6 } });
    map.addLayer({ id: 'ov-obj-point', type: 'circle', source: 'ov-ent', 'source-layer': 'points',
      // Damage is excluded here and drawn by its own red layers below, so the
      // two groups can be toggled apart and never double-draw.
      filter: ['all', ['!=', ['get', 'type_key'], 'live_event'],
                      ['!=', ['get', 'type_key'], 'damage_marker']] as never,
      layout: hidden,
      paint: { 'circle-radius': 4, 'circle-color': '#38bdf8',
               'circle-stroke-color': '#0b1b2b', 'circle-stroke-width': 1.5 } });
    // Damage already on the map, in the same red the new markers use, so
    // "already recorded" and "just recorded" read as one thing.
    map.addLayer({ id: 'ov-dmg-fill', type: 'fill', source: 'ov-ent', 'source-layer': 'polygons',
      filter: ['==', ['get', 'type_key'], 'damage_marker'] as never, layout: hidden,
      paint: { 'fill-color': RED, 'fill-opacity': 0.14 } });
    map.addLayer({ id: 'ov-dmg-outline', type: 'line', source: 'ov-ent', 'source-layer': 'polygons',
      filter: ['==', ['get', 'type_key'], 'damage_marker'] as never, layout: hidden,
      paint: { 'line-color': RED, 'line-width': 1.6 } });
    map.addLayer({ id: 'ov-dmg-pt', type: 'circle', source: 'ov-ent', 'source-layer': 'points',
      filter: ['==', ['get', 'type_key'], 'damage_marker'] as never, layout: hidden,
      paint: { 'circle-radius': 5, 'circle-color': RED,
               'circle-stroke-color': '#ffffff', 'circle-stroke-width': 1.4,
               'circle-opacity': 0.85 } });
    map.addLayer({ id: 'ov-live', type: 'circle', source: 'ov-ent', 'source-layer': 'points',
      filter: ['==', ['get', 'type_key'], 'live_event'] as never, layout: hidden,
      paint: { 'circle-radius': 5, 'circle-color': '#f6a723',
               'circle-stroke-color': '#ffffff', 'circle-stroke-width': 1.2, 'circle-opacity': 0.9 } });
    // Same flame-triangle mark as the main map's FIRMS layer, so hotspots
    // never read as live-event dots here either.
    installFirmsImages(map);
    map.addLayer({ id: 'ov-firms', type: 'symbol', source: 'ov-firms-src',
      layout: { ...hidden, 'icon-image': 'firms-tri-n', 'icon-size': 0.45,
                'icon-allow-overlap': true, 'icon-ignore-placement': true },
      paint: { 'icon-opacity': 0.9 } });
    overlaysReady = true;
  }

  async function loadFirmsOverlay(): Promise<void> {
    const bb = sceneBbox ?? scanBbox;
    const qs = new URLSearchParams({
      west: String(bb[0]), south: String(bb[1]), east: String(bb[2]), north: String(bb[3]),
      days: '7',
    });
    try {
      const res = await fetch(`/api/firms?${qs}`);
      const data = await res.json().catch(() => ({}));
      (map.getSource('ov-firms-src') as maplibregl.GeoJSONSource | undefined)
        ?.setData(res.ok ? data : { type: 'FeatureCollection', features: [] });
    } catch { /* leave the layer empty */ }
  }

  function setOverlayVisible(ids: string[], on: boolean): void {
    for (const id of ids) if (map.getLayer(id)) {
      map.setLayoutProperty(id, 'visibility', on ? 'visible' : 'none');
    }
  }

  overlay.querySelectorAll<HTMLInputElement>('[data-ov]').forEach((cb) =>
    cb.addEventListener('change', async () => {
      const key = cb.dataset.ov!;
      await ensureOverlays();
      if (closed) return;
      if (key === 'firms' && cb.checked) await loadFirmsOverlay();
      raiseOverlays();
      setOverlayVisible(OV_GROUP[key] ?? [], cb.checked);
    }));

  // --- boundary redraw (opened from a place only) ---------------------------
  // Redraw the place's boundary on the imagery itself — a polygon (click
  // corners, then Finish) or a box (two opposite corners) — and PUT it as the
  // entity's new geometry. Monitoring, "Update all" and future Studio opens use
  // the new outline, and this session re-frames onto it: the operator drew it
  // while looking at the imagery, so it is also the area they want to analyse.
  let drawingBoundary = false;
  let boundaryPts: [number, number][] = [];
  let boundaryCleanup: (() => void) | null = null;

  function ensureBoundaryLayers(): void {
    if (map.getSource('bnd-draw')) return;
    map.addSource('bnd-draw',
      { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
    map.addLayer({ id: 'bnd-fill', type: 'fill', source: 'bnd-draw',
      filter: ['==', ['geometry-type'], 'Polygon'],
      paint: { 'fill-color': '#ffb300', 'fill-opacity': 0.16 } });
    map.addLayer({ id: 'bnd-casing', type: 'line', source: 'bnd-draw',
      filter: ['!=', ['geometry-type'], 'Point'],
      paint: { 'line-color': '#ffffff', 'line-width': 4.5, 'line-opacity': 0.85 } });
    map.addLayer({ id: 'bnd-line', type: 'line', source: 'bnd-draw',
      filter: ['!=', ['geometry-type'], 'Point'],
      paint: { 'line-color': '#ff8f00', 'line-width': 2.2, 'line-dasharray': [2, 1.3] } });
    map.addLayer({ id: 'bnd-verts', type: 'circle', source: 'bnd-draw',
      filter: ['==', ['geometry-type'], 'Point'],
      paint: { 'circle-radius': 5, 'circle-color': '#ffffff',
               'circle-stroke-color': '#ff8f00', 'circle-stroke-width': 2.4 } });
  }

  function boundaryPreview(cursor: [number, number] | null,
                           boxMode: boolean): void {
    const src = map.getSource('bnd-draw') as maplibregl.GeoJSONSource | undefined;
    if (!src) return;
    const feats: GeoJSON.Feature[] = [];
    if (boxMode && boundaryPts.length === 1 && cursor) {
      const [ax, ay] = boundaryPts[0]!;
      feats.push({ type: 'Feature', properties: {}, geometry: { type: 'Polygon',
        coordinates: [[[ax, ay], [cursor[0], ay], cursor, [ax, cursor[1]], [ax, ay]]] } });
    } else if (!boxMode) {
      const line = cursor ? [...boundaryPts, cursor] : boundaryPts;
      if (line.length >= 2) {
        feats.push({ type: 'Feature', properties: {},
          geometry: { type: 'LineString', coordinates: line } });
      }
      if (boundaryPts.length >= 3) {
        feats.push({ type: 'Feature', properties: {}, geometry: { type: 'Polygon',
          coordinates: [[...boundaryPts, boundaryPts[0]!]] } });
      }
    }
    for (const p of boundaryPts) {
      feats.push({ type: 'Feature', properties: {},
        geometry: { type: 'Point', coordinates: p } });
    }
    src.setData({ type: 'FeatureCollection', features: feats });
  }

  function stopBoundaryDraw(): void {
    boundaryCleanup?.();
    boundaryCleanup = null;
    drawingBoundary = false;
  }

  async function saveBoundary(ring: [number, number][]): Promise<void> {
    const geom: Polygon = { type: 'Polygon', coordinates: [ring] };
    try {
      await put(`/api/entities/${place!.id}`, { geometry: geom });
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Could not update the boundary.');
      return;
    }
    const named = place!.name ?? 'this place';
    // A small compound can sit under the scan floor, and the boundary is saved
    // either way — so the re-frame is the optional half of this, never the
    // headline.
    const framed = await setScanArea(geom, `Boundary of ${named} updated — monitoring `
      + 'and future scans use it, and this Studio is now framed to it.');
    if (!framed) {
      toast(`Boundary of ${named} updated — monitoring and future scans use it.`);
    }
  }

  /** Click-a-ring on the imagery. Shared by the place boundary and by damage
   *  areas — same affordance, same cleanup, different destination. `onFinish`
   *  receives a CLOSED ring (last point == first). */
  function beginPolyDraw(boxMode: boolean, title: string,
                         onFinish: (ring: [number, number][]) => void): void {
    stopBoundaryDraw();
    // A box is two corners of a NORTH-UP rectangle — scan.ts's rule, on this
    // map — so a bearing tilts it and a pitch makes it a trapezoid, and either
    // way it is not the shape that was drawn. Level the camera. Unlike the
    // cockpit this view is not restored afterwards: the scenes are north-up
    // rasters, level is where this map belongs, and finishing a box can hand
    // straight to setScanArea's fitBounds — two camera animations racing would
    // strand the map at whatever bearing the frame in between happened to hold.
    // A polygon is its clicked points exactly, so it needs none of this.
    let levelling = false;
    if (boxMode && (Math.abs(map.getBearing()) > 0.5 || map.getPitch() > 0.5)) {
      levelling = true;
      map.easeTo({ bearing: 0, pitch: 0, duration: 350 });
      map.once('moveend', () => { levelling = false; });
      toast('Levelled the map — a box is north-up.');
    }
    drawingBoundary = true;
    boundaryPts = [];
    ensureBoundaryLayers();
    boundaryPreview(null, boxMode);
    map.getCanvas().style.cursor = 'crosshair';
    map.doubleClickZoom.disable();
    const finish = (ring: [number, number][]): void => {
      stopBoundaryDraw();
      onFinish(ring);
    };
    // Finish/Undo/Cancel, because a double TAP cannot end a shape on a phone.
    // Box mode needs no Finish — the second corner completes it on its own.
    const bar = showDrawBar({
      surface: map.getCanvas(),
      title,
      minPoints: boxMode ? 2 : 3, pointShortcuts: true,
      hint: boxMode ? 'Click two opposite corners to finish the rectangle.' : 'Click corners; click the first corner or press Enter to finish. Backspace undoes.',
      minHint: boxMode ? 'Click two opposite corners' : 'Click at least 3 corners',
      onFinish: () => {
        if (boxMode || boundaryPts.length < 3) return;
        finish([...boundaryPts, boundaryPts[0]!]);
      },
      onUndo: () => {
        boundaryPts.pop();
        boundaryPreview(null, boxMode);
        bar.update(boundaryPts.length);
      },
      onCancel: () => stopBoundaryDraw(),
    });
    const onClick = (ev: maplibregl.MapMouseEvent): void => {
      // Mid-levelling the ground is still moving under the cursor, and the
      // corner would land wherever that frame happened to be. Guarded on our
      // own flag rather than isMoving(), which is also true through the
      // inertia after a pan — a click there is a real one.
      if (levelling) return;
      if (ev.originalEvent.detail > 1) return;
      if (!boxMode && boundaryPts.length >= 3) {
        const first = map.project(boundaryPts[0]!);
        if (Math.hypot(ev.point.x - first.x, ev.point.y - first.y) <= 12) {
          finish([...boundaryPts, boundaryPts[0]!]); return;
        }
      }
      boundaryPts.push([ev.lngLat.lng, ev.lngLat.lat]);
      if (boxMode && boundaryPts.length === 2) {
        const [[ax, ay], [bx, by]] = boundaryPts as [[number, number], [number, number]];
        finish([[ax, ay], [bx, ay], [bx, by], [ax, by], [ax, ay]]);
        return;
      }
      boundaryPreview(null, boxMode);
      bar.update(boundaryPts.length);
    };
    const onMove = (ev: maplibregl.MapMouseEvent): void => {
      if (boundaryPts.length) boundaryPreview([ev.lngLat.lng, ev.lngLat.lat], boxMode);
    };
    // Kept as the mouse shortcut it always was, now that it is not the only way.
    const onDbl = (ev: maplibregl.MapMouseEvent): void => {
      ev.preventDefault();
      if (boxMode) return;
      if (boundaryPts.length < 3) { toast('Need at least three corners.'); return; }
      finish([...boundaryPts, boundaryPts[0]!]);
    };
    map.on('click', onClick);
    map.on('mousemove', onMove);
    map.on('dblclick', onDbl);
    boundaryCleanup = () => {
      bar.close();
      map.off('click', onClick);
      map.off('mousemove', onMove);
      map.off('dblclick', onDbl);
      map.doubleClickZoom.enable();
      map.getCanvas().style.cursor = '';
      (map.getSource('bnd-draw') as maplibregl.GeoJSONSource | undefined)
        ?.setData({ type: 'FeatureCollection', features: [] });
    };
  }

  function beginBoundaryDraw(boxMode: boolean): void {
    beginPolyDraw(boxMode, `Boundary of ${place?.name ?? 'this place'}`,
                  (ring) => void saveBoundary(ring));
  }

  if (place) {
    const boundaryPod = q<HTMLDivElement>('.studio-boundary');
    (boundaryPod.querySelector('.bnd-name') as HTMLElement).textContent =
      place.name ?? 'this place';
    q<HTMLButtonElement>('[data-act="boundary"]').addEventListener('click', () => {
      boundaryPod.style.display = boundaryPod.style.display === 'none' ? 'flex' : 'none';
      if (boundaryPod.style.display === 'none') stopBoundaryDraw();
    });
    boundaryPod.querySelectorAll<HTMLButtonElement>('[data-bnd]').forEach((b) =>
      b.addEventListener('click', () => {
        const kind = b.dataset.bnd!;
        if (kind === 'cancel') {
          stopBoundaryDraw();
          boundaryPod.style.display = 'none';
          return;
        }
        areaPod.style.display = 'none';    // one pod owns the slot under the bar
        beginBoundaryDraw(kind === 'box');
      }));
  }

  // --- the area under analysis ---------------------------------------------
  //
  // The box drawn on the way in is a first guess, and what is worth looking at
  // is regularly just outside it. Re-framing IN PLACE keeps the session — the
  // cloud and look-back filters, the view mode, every analyzer setting and the
  // markup — where re-opening the Studio throws all of it away.
  //
  // Everything derived from the old footprint has to go with it: the decoded
  // bands, the alignment, the composite/change/mask memos and the scene bbox
  // all belong to the geometry that produced them, and a survivor would paint
  // old pixels onto new ground while looking entirely plausible.

  function bboxOfPolygon(g: Polygon): [number, number, number, number] {
    let w = Infinity, s = Infinity, e = -Infinity, n = -Infinity;
    for (const ring of g.coordinates) {
      for (const pt of ring) {
        const [x, y] = pt as [number, number];
        if (x < w) w = x;
        if (x > e) e = x;
        if (y < s) s = y;
        if (y > n) n = y;
      }
    }
    return [w, s, e, n];
  }

  /** Returns false when the area was refused, so a caller that has already
   *  done something else (saving a place boundary) can report that honestly
   *  instead of letting the refusal stand as the whole outcome. */
  async function setScanArea(geom: Polygon, note: string): Promise<boolean> {
    stopBoundaryDraw();
    const box = bboxOfPolygon(geom);
    // The same floor scan.ts puts on a dragged box: below it the render comes
    // back as a handful of pixels, or the request simply fails.
    if (!(box[2] - box[0] > 0.002 && box[3] - box[1] > 0.002)) {
      toast('That area is too small to scan — draw a larger one.');
      return false;
    }
    scanGeometry = geom;
    scanBbox = box;
    coordsLabel = fmtCoords(box);
    coordsEl.textContent = coordsLabel;

    loadToken += 1;      // strand any fetch in flight for the old footprint
    recompToken += 1;
    stopBlink();
    dropScenes();        // the stack and the decoded scenes are the old footprint's
    track = null;        // a new area has its own best orbit
    tracks = [];
    sceneBbox = null;
    for (const key of ['before', 'after', 'change'] as const) {
      paintScene(key, null);
      setOpacity(key, 0);
    }
    dates = [];
    dateA = null;
    dateB = null;
    extras = [];
    map.fitBounds([[box[0], box[1]], [box[2], box[3]]], { padding: 90, duration: 400 });
    toast(note);
    await loadDates(true);
    // FIRMS was fetched for the old box, so it is now showing the wrong place.
    if (!closed && overlay.querySelector<HTMLInputElement>('[data-ov="firms"]')?.checked) {
      await loadFirmsOverlay();
    }
    return true;
  }

  // Redrawing is the main map's gesture: press ▣ Redraw area and DRAG a box
  // (scan.ts's own drag, armed on this map). It used to be a pod of choices —
  // This view / click two corners / polygon — that nobody found quicker than
  // closing the Studio and dragging a new box on the main map, which threw the
  // session away. The current area is outlined while the pod is up, so what is
  // being replaced is visible; the polygon, the view and the opening area are
  // still one press away.
  const areaPod = q<HTMLDivElement>('.studio-area');
  const areaBtn = q<HTMLButtonElement>('[data-act="area"]');
  function showAreaOutline(on: boolean): void {
    if (!map.getSource('area-now')) {
      if (!on) return;
      map.addSource('area-now', { type: 'geojson', data: scanGeometry });
      map.addLayer({ id: 'area-now-casing', type: 'line', source: 'area-now',
        paint: { 'line-color': '#ffffff', 'line-width': 4, 'line-opacity': 0.8 } });
      map.addLayer({ id: 'area-now-line', type: 'line', source: 'area-now',
        paint: { 'line-color': '#55d6be', 'line-width': 2, 'line-dasharray': [2, 1.5] } });
    }
    (map.getSource('area-now') as maplibregl.GeoJSONSource).setData(scanGeometry);
    for (const id of ['area-now-casing', 'area-now-line']) {
      map.setLayoutProperty(id, 'visibility', on ? 'visible' : 'none');
      if (on) map.moveLayer(id);
    }
  }
  function armAreaDrag(): void {
    // Markup and the damage tools read the same presses on this canvas.
    if (isMarkupActive(map)) { closeMarkup(map); syncRailButtons(); }
    if (damageTool) setDamageTool(null);
    stopBoundaryDraw();
    let taken = false;
    startScanDraw(map, (box) => {
      taken = true;
      showAreaPod(false);
      void setScanArea(boxPolygon(box), 'New area — re-listing scenes.');
    }, () => {
      // A tap or a too-small box: stay armed for another try.
      if (!taken && !closed && areaPod.style.display !== 'none') armAreaDrag();
    });
  }
  function showAreaPod(on: boolean): void {
    areaPod.style.display = on ? 'flex' : 'none';
    areaBtn.classList.toggle('active', on);
    showAreaOutline(on);
    if (on) armAreaDrag();
    else { cancelScanDraw(map); stopBoundaryDraw(); }
  }
  areaBtn.addEventListener('click', () => {
    const boundaryPod = overlay.querySelector<HTMLDivElement>('.studio-boundary');
    if (boundaryPod) boundaryPod.style.display = 'none';
    showAreaPod(areaPod.style.display === 'none');
  });
  areaPod.querySelectorAll<HTMLButtonElement>('[data-ar]').forEach((b) =>
    b.addEventListener('click', () => {
      const kind = b.dataset.ar!;
      if (kind === 'cancel') { showAreaPod(false); return; }
      cancelScanDraw(map);
      if (kind === 'view') {
        const v = map.getBounds();
        showAreaPod(false);
        void setScanArea(boxPolygon([v.getWest(), v.getSouth(), v.getEast(), v.getNorth()]),
                         'Analysing what is on screen — re-listing scenes.');
        return;
      }
      if (kind === 'reset') {
        showAreaPod(false);
        void setScanArea(openArea, 'Back to the area this Studio opened with.');
        return;
      }
      beginPolyDraw(false, 'Area to analyse', (ring) => {
        showAreaPod(false);
        void setScanArea({ type: 'Polygon', coordinates: [ring] },
                         'New area — re-listing scenes.');
      });
    }));

  // --- recording damage from the imagery -----------------------------------
  //
  // The point of a before/after light table is to find what was hit, so the
  // call gets made here rather than remembered and re-found on the main map.
  //
  // Two things make a marker made here different from one dropped by hand:
  //
  // 1. IT IS DATED BY THE IMAGERY, NOT BY TODAY. Damage markers age by
  //    attrs.date — mvt_entities stamps the tile's `ts` from it for this type
  //    alone — so a marker made in August about June scenes would otherwise
  //    sort as a fresh strike and land in the wrong end of the Layers panel's
  //    damage window. The honest date is B: the first scene it was seen
  //    damaged in.
  // 2. It carries the pair it was read from (migration 030), so the claim
  //    stays checkable: intact in A, damaged in B, read off this view.
  //
  // The markup tools above are a different thing entirely — annotation that
  // lives on an exported PNG. These write an entity everyone can see.

  let damageTool: 'point' | 'area' | null = null;

  /** What the operator was actually looking at, for attrs.evidence. Recorded
   *  as shown rather than as an internal key, because the reader of a damage
   *  record is a human deciding whether to trust it. */
  function evidenceLabel(): string {
    const sensor = source === 'sar' ? `Sentinel-1 SAR${track ? ` orbit ${track}` : ''}`
      : isViirs(source) ? `VIIRS ${nameOf(dateB)}` : 'Sentinel-2';
    const view = showChange && !isViirs(source)
      ? LAYER_LABELS[changeLayer] ?? changeLayer
      : styleLabelsFor(source)[style] ?? style;
    // The baseline is part of what the operator was looking at, so the record
    // says so — "read against 6 scenes" is a stronger claim than one pair.
    const deepNote = deep ? ` · baseline ×${deep.baseDates.length}` : '';
    return `${sensor}${deepNote} · ${view}`;
  }

  function ensureDamageLayers(): void {
    if (map.getSource('dmg-new')) return;
    map.addSource('dmg-new',
      { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
    map.addLayer({ id: 'dmg-new-fill', type: 'fill', source: 'dmg-new',
      filter: ['==', ['geometry-type'], 'Polygon'],
      paint: { 'fill-color': RED, 'fill-opacity': 0.22 } });
    map.addLayer({ id: 'dmg-new-line', type: 'line', source: 'dmg-new',
      filter: ['==', ['geometry-type'], 'Polygon'],
      paint: { 'line-color': RED, 'line-width': 2 } });
    map.addLayer({ id: 'dmg-new-pt', type: 'circle', source: 'dmg-new',
      filter: ['==', ['geometry-type'], 'Point'],
      paint: { 'circle-radius': 6, 'circle-color': RED,
               'circle-stroke-color': '#ffffff', 'circle-stroke-width': 2 } });
  }

  // Markers written this session, drawn client-side. The ov-ent tiles will not
  // carry them until Martin's cache turns over, and this is the same
  // instant-feedback source objects.ts keeps on the main map for exactly that
  // reason — without it you cannot tell a saved marker from a lost click.
  const addedDamage: GeoJSON.Feature[] = [];

  function showAddedDamage(geometry: GeoJSON.Geometry): void {
    // Guarded: the marker is already SAVED by the time this runs, so a style
    // that is not ready must not throw past the toast and the cockpit refresh.
    // Losing the local echo is recoverable; losing the notification is not.
    try {
      ensureDamageLayers();
      addedDamage.push({ type: 'Feature', properties: {}, geometry });
      (map.getSource('dmg-new') as maplibregl.GeoJSONSource | undefined)
        ?.setData({ type: 'FeatureCollection', features: addedDamage });
    } catch {
      /* the record is safe; only its echo on this canvas is missing */
    }
  }

  /** Returns true when a marker was written, so the caller can decide whether
   *  to re-arm — after a failure, re-arming just invites the same failure. */
  async function saveDamage(geometry: GeoJSON.Geometry): Promise<boolean> {
    const label = prompt('Label this damage (what was hit):')?.trim() ?? '';
    // Cancelling the prompt must not silently write an unlabelled marker.
    if (label === '' && !confirm('Save this damage marker without a label?')) return false;

    // Order the pair by DATE, not by which slot it is in. The A/B slots are a
    // display choice the operator can Swap freely, but a damage record has to
    // be dated by the later OBSERVATION — "seen damaged by" only means
    // anything chronologically. ISO dates compare correctly as strings.
    const [seen0, seen1] = dateA && dateB
      ? [dateA, dateB].sort() as [string, string]
      : [null, null];
    // A VIIRS key is an overpass INSTANT; the record's fields are dates.
    const attrs: Record<string, string> = {
      date: (seen1 ?? dateB ?? dateA ?? new Date().toISOString()).slice(0, 10),
    };
    // Only when a real pair is loaded. Marking on the basemap with no imagery
    // is legitimate, and inventing provenance for it would be worse than none.
    if (seen0 && seen1) {
      attrs.scene_before = seen0.slice(0, 10);
      attrs.scene_after = seen1.slice(0, 10);
      attrs.evidence = evidenceLabel();
    }
    try {
      const created = await post<EntityDto>('/api/entities', {
        type: 'damage_marker', name: label || null, attrs, geometry,
      });
      showAddedDamage(geometry);
      // Name the date back, because it is deliberately NOT today and a silent
      // surprise here would be discovered weeks later on the damage timeline.
      toast(seen0 && seen1
        ? `Damage recorded, dated ${attrs.date} (intact ${nameOf(seen0)}, damaged ${nameOf(seen1)}).`
        : `Damage recorded, dated ${attrs.date}.`);
      hooks?.onEntityAdded?.(created);
      return true;
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Could not save the damage marker.');
      return false;
    }
  }

  /** Show what is already recorded here, and tick the box so the state is
   *  legible rather than magic. Idempotent; safe to call on every arm. */
  async function showExistingDamage(): Promise<void> {
    const cb = overlay.querySelector<HTMLInputElement>('[data-ov="damage"]');
    if (!cb || cb.checked) return;
    cb.checked = true;
    await ensureOverlays();
    if (closed) return;
    raiseOverlays();
    setOverlayVisible(OV_GROUP.damage ?? [], true);
  }

  /** Arm or disarm. Deliberately silent — the re-arm after each save would
   *  otherwise talk over the "Damage recorded" toast every single time. The
   *  button handler says it once, when the operator asks for the tool. */
  function setDamageTool(tool: 'point' | 'area' | null): void {
    stopBoundaryDraw();
    if (tool) { closeMarkup(map); syncRailButtons(); }          // annotation and recording are exclusive
    damageTool = tool;
    overlay.querySelectorAll<HTMLButtonElement>('[data-dmg]').forEach((btn) =>
      btn.classList.toggle('active', btn.dataset.dmg === tool));
    map.getCanvas().style.cursor = tool === 'point' ? 'crosshair' : '';
  }

  // A tap fires 'click' on MapLibre, so the point tool needs no pointer-event
  // dance — unlike the markup drag tools, which do (see scan.ts / rotate.ts).
  map.on('click', async (ev) => {
    if (damageTool !== 'point') return;
    const geometry: GeoJSON.Geometry =
      { type: 'Point', coordinates: [ev.lngLat.lng, ev.lngLat.lat] };
    // Disarm across the modal prompt, then RE-ARM on success: reviewing a pair
    // usually turns up several hits, and re-clicking the tool between each one
    // is friction on the one workflow this exists for. A stray click is not
    // costly here — every save is gated by the label prompt.
    setDamageTool(null);
    if (await saveDamage(geometry)) setDamageTool('point');
  });

  overlay.querySelectorAll<HTMLButtonElement>('[data-dmg]').forEach((btn) =>
    btn.addEventListener('click', () => {
      const kind = btn.dataset.dmg as 'point' | 'area';
      if (damageTool === kind) { setDamageTool(null); return; }
      // Arming the tool turns on the "already recorded" overlay: the first
      // thing you need while marking is what is already there, and defaulting
      // that overlay ON for everyone would pull entity tiles into every Studio
      // session that never marks anything.
      void showExistingDamage();
      if (kind === 'point') {
        setDamageTool('point');
        const dated = dateA && dateB ? [dateA, dateB].sort()[1] : dateB ?? dateA;
        toast(dated
          ? `Click each damaged spot — they will be dated ${dated}, the later scene. Esc stops.`
          : 'Click each damaged spot. Esc stops.');
        return;
      }
      setDamageTool('area');
      beginPolyDraw(false, 'Damaged area', (ring) => {
        setDamageTool(null);
        void saveDamage({ type: 'Polygon', coordinates: [ring] });
      });
    }));

  // --- keyboard ------------------------------------------------------------
  function onKey(ev: KeyboardEvent): void {
    if (isMapEditing() && ev.key !== 'Escape') return;
    if (ev.key === 'Escape') {
      // Ordered by how surprising it would be to lose: a crop drag, then a
      // shape in progress, then a tool, and only then the Studio itself. Both
      // handlers are on window in the capture phase and this one is registered
      // first, so without the check here Esc during a crop closes everything.
      // The flyout hangs off the markup bar, so it goes before the bar does.
      if (sheetEscape(map)) return;
      if (drawingBoundary) { stopBoundaryDraw(); setDamageTool(null); return; }
      if (damageTool) { setDamageTool(null); return; }
      if (markupEscape(map)) { syncRailButtons(); return; }
      // Open pods last of all but still ahead of close(): re-opening one costs
      // a click, where closing the Studio costs the whole session.
      if (areaPod.style.display !== 'none') { showAreaPod(false); return; }
      const bp = overlay.querySelector<HTMLDivElement>('.studio-boundary');
      if (bp && bp.style.display !== 'none') { bp.style.display = 'none'; return; }
      close();
      return;
    }
    if (ev.target instanceof HTMLInputElement || ev.target instanceof HTMLSelectElement) return;
    if (ev.key === 'ArrowLeft') { ev.preventDefault(); moveB(-1); }
    else if (ev.key === 'ArrowRight') { ev.preventDefault(); moveB(1); }
    else if (ev.key === ' ' && mode === 'blink') { ev.preventDefault(); togglePlay(); }
  }
  window.addEventListener('keydown', onKey, true);

  // --- close ---------------------------------------------------------------
  function close(): void {
    if (closed) return;
    closed = true;
    loadToken += 1;
    recompToken += 1;
    deepToken += 1;
    stopBlink();
    if (paintTimer !== null) {
      clearTimeout(paintTimer);
      paintTimer = null;
    }
    window.removeEventListener('keydown', onKey, true);
    cancelScanDraw(map);   // a Redraw-area drag must not outlive its map
    closeMarkup(map);
    map.remove();
    overlay.remove();
    studioOpen = false;
  }

  // --- boot: list the scenes, fetch nothing --------------------------------
  // The catalogue is free and the pair has to be pickable before it is paid
  // for, so the strip is populated on open. Pixels wait for the Load button.
  await mapReady;
  if (closed) return;
  // Before the sheet: a saved sheet names its basemap.
  installBasemaps(map, { ownStyle: false });
  setBasemap(map, basemapSel.value);
  // Markup and the sheet: the same engine and composition as the cockpit's.
  installMarkupLayers(map);
  registerSheetHost(sheetHost);
  const sheet = opts?.sheet;
  if (sheet) {
    await useSheet(map, sheet);
    applySheetImagerySettings(sheet.imagery);
  } else {
    startDraft(map);
  }
  if (!closed) await loadDates(true);
  if (!closed && sheet?.imagery) applySheetPair(sheet.imagery);
}
